// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useRef, useState, FC, useCallback, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from 'Hooks';
import { useSelector } from 'react-redux';
import { RootState } from 'Store';
import SVG from 'react-inlinesvg';
import Map from '@arcgis/core/Map';
import Ground from '@arcgis/core/Ground';
import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import Zoom from '@arcgis/core/widgets/Zoom.js';
import NavigationToggle from '@arcgis/core/widgets/NavigationToggle.js';
import Compass from '@arcgis/core/widgets/Compass.js';
import Home from '@arcgis/core/widgets/Home.js';
import Viewpoint from '@arcgis/core/Viewpoint.js';
import Measurement from '@arcgis/core/widgets/Measurement.js';
import DistanceMeasurement2D from '@arcgis/core/widgets/DistanceMeasurement2D';
import AreaMeasurement2D from '@arcgis/core/widgets/AreaMeasurement2D';
import FeatureFilter from '@arcgis/core/layers/support/FeatureFilter';
import DirectLineMeasurement3D from '@arcgis/core/widgets/DirectLineMeasurement3D';
import AreaMeasurement3D from '@arcgis/core/widgets/AreaMeasurement3D';
import PointCloudLayer from '@arcgis/core/layers/PointCloudLayer';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer.js';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import * as query from '@arcgis/core/rest/query';
import Query from '@arcgis/core/rest/support/Query';
import LabelClass from '@arcgis/core/layers/support/LabelClass';
import esriRequest from '@arcgis/core/request';
import Color from '@arcgis/core/Color';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import BaseTileLayer from '@arcgis/core/layers/BaseTileLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import * as identify from '@arcgis/core/rest/identify';
import IdentifyParameters from '@arcgis/core/rest/support/IdentifyParameters';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import Graphic from '@arcgis/core/Graphic';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import BasemapGallery from '@arcgis/core/widgets/BasemapGallery.js';
import * as projectOperator from '@arcgis/core/geometry/operators/projectOperator.js';
import * as distanceOperator from '@arcgis/core/geometry/operators/distanceOperator.js';
import '@arcgis/core/assets/esri/themes/light/main.css';
import '@esri/calcite-components/dist/components/calcite-switch';
import '@esri/calcite-components/dist/calcite/calcite.css';
import Point from '@arcgis/core/geometry/Point';
import { GetProjectDetailsRoute } from 'Utils/routes';
import ReactPannellum, { getConfig } from 'react-pannellum';
import type { ElevationInfo } from '@arcgis/core/layers/support/ElevationInfo';
import Extent from '@arcgis/core/geometry/Extent';
import { Box } from '@mui/system';
import { useNavigate, useParams } from 'react-router-dom';
import Slider from '@arcgis/core/widgets/Slider';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import { API_URL } from 'Config';

// Redux selectors
import eyeVisible from 'Assets/view-visible.svg';
import eyeHidden from 'Assets/view-hidden.svg';
import { orderStatusCode } from 'Utils/constants';
import { fullPointCloudPalette, getLegendClasses, getNamedPointCloudClasses, getPointCloudPalette, parsePresentClassIds } from 'Utils/pointCloudClasses';
import { IconButton, SvgIcon, Typography, ClickAwayListener, CircularProgress, Switch, FormControlLabel, Button } from '@mui/material';
import { getOrders } from 'Features/order/orderSelectors';
import { SetOrderSelectedAction } from 'Features/map/mapEditorActions';
import { forEach } from 'jszip';
import { ResetKmlAction } from 'Features/kml/kmlActions';
import { GetEsriGeoJsonUrl } from 'Config';
import getRendererForConfig from 'Utils/esriRenderer';
import AreaMeasurement from './AreaCustomWidget';
import DistanceMeasurement from './DistanceCustomWidget';
import MeasurementUnitToggle from './MeasurementUnitToggle';
import showChatHighlight, { clearChatHighlight } from './chatMapHighlight';
import GdbRowsPanel from './GdbRowsPanel';
import { setLoadedGdbId, setPanoVisionActive } from 'Features/dashboard/dashboardSlice';
import { SaveGdbElevationThunk, EnsureGeoidOffsetThunk, EnsureGeoidOffsetResult, EnsureGeoidOffsetReason } from './elevationThunk';
import { resolveUserElevationOffset, resolveGroundOffset, resolveOrderGeoidOffset, resolvePendingGeoidState, resolvePlacedGeoidResolution } from './elevationOffset';
import { registerPanoCapturer, cropEquirectToView } from 'Features/chatbot/panoImage';
import Dashboard from 'Features/dashboard';
import { setSelectedGdbFeature, setSelectedRegion, SelectedGdbFeature } from 'Features/dashboard/dashboardSlice';
import { createRegionSketchController, RegionSketchController } from './regionSelect';
import Annotation from './Annotation';
import LabelEditor from './Label/LabelEditor';
import TerrainWidget from './TerrainWidget';
import Basemap from './Basemap';
import AOI from './AOIWidget';
import { getFeatureCollection } from '../kml/kmlSelectors';
import FilesSidebar from './Sidebar/index';
import { GeoidStatus } from './Sidebar/ElevationOffsetInfo';
import { OffsetElevationLayer } from './OffsetElevationLayer';
import { ResetProjectAction } from '../project/projectActions';
import { ProjectNotFound } from '../project/ProjectNotFound';
import stylesDef from '../files/styles';
import getStyles, { COMPASS_RADIUS_PX, COMPASS_SQUASH_Y } from './styles';
import config from './mainconfig';
import { useArcgisToken } from './useArcgisToken';
import { useViewerProjectLoad } from './useViewerProjectLoad';
import { ViewerTokenError } from './ViewerTokenError';
import boundaryRenderer, { selectOrderBoundaries } from './boundaryRenderer';
import { useFlightTargets } from './useFlightTargets';
// DEPRECATED (AUT-6108): getPanoNeighborsThunk is imported but no longer called —
// pano neighbor selection moved client-side (see updateNavArrows). Kept for now
// pending removal of the thunk; safe to drop from this import at that point.
import { getSignedURLThunk, getPanoNeighborsThunk } from './esriSplitViewerThunk';
import { ClearAnnotationSuccessAction, ClearPanoFeaturesAction } from './esriSplitViewerActions';
import PopupWindow from './PopupWindow';
import KhoraChatbot from 'Features/chatbot';
import { KHORA_ENABLED, isKhoraUiEnabled } from 'Features/chatbot/constants';
import { styleConfig } from './styleConfig';
import { findRule } from './rendererRules';

// Summarize a clicked geodatabase graphic for the chatbot (serializable): its layer name + a compact
// set of non-null attributes (capped), so Khora can resolve "this feature / the selected pole".
const summarizeGdbFeature = (graphic: __esri.Graphic, layer: __esri.Layer): SelectedGdbFeature | null => {
  const layerName = (layer as { title?: string; layerName?: string })?.title
    || (layer as { layerName?: string })?.layerName;
  if (!layerName) return null;
  const attributes: Record<string, string | number> = {};
  let count = 0;
  Object.entries(graphic.attributes || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    if (typeof value !== 'string' && typeof value !== 'number') return;
    if (count >= 25) return; // keep app_state compact
    attributes[key] = value;
    count += 1;
  });
  return { layer: String(layerName), attributes };
};

// A flat single-color renderer for a Khora Analysis Layer (pinned highlight / add_spatial_layer), so it
// draws in its DECLARED color (matching its Analysis Layers swatch + popup header) instead of Esri's default
// symbol. geometryType is known only after the layer loads. Point → marker, line → line, polygon →
// translucent fill + solid outline. `color` may be a CSS name ('green') or #hex — Color parses both.
const analysisLayerRenderer = (geometryType: string | null | undefined, color: string): SimpleRenderer => {
  const col = new Color(color);
  if (geometryType === 'point' || geometryType === 'multipoint') {
    return new SimpleRenderer({ symbol: { type: 'simple-marker', color: col, size: 8, outline: { color: [255, 255, 255, 1], width: 1 } } as any });
  }
  if (geometryType === 'polyline') {
    return new SimpleRenderer({ symbol: { type: 'simple-line', color: col, width: 2.5 } as any });
  }
  const fill = col.clone(); fill.a = 0.25;
  return new SimpleRenderer({ symbol: { type: 'simple-fill', color: fill, outline: { color: col, width: 1.5 } } as any });
};

const RasterTileLayer = BaseTileLayer.createSubclass({
  properties: {
    urlTemplate: null,
    isTms: false,
    tint: {
      value: null,
      type: Color,
    },
  },
  initialize() {
    reactiveUtils.watch(
      () => this.tint,
      () => {
        this.refresh();
      },
    );
  },
  getTileUrl(level, row, col) {
    let y = row; // default XYZ
    if (this.isTms) {
      y = 2 ** level - row - 1; // flip for TMS
    }
    return this.urlTemplate
      .replace('{z}', level)
      .replace('{x}', col)
      .replace('{y}', y);
  },
  fetchTile(level, row, col, options) {
    const url = this.getTileUrl(level, row, col);
    return esriRequest(url, {
      responseType: 'image',
      signal: options?.signal,
    }).then(
      (response) => {
        const image = response.data;
        const width = this.tileInfo.size[0];
        const height = this.tileInfo.size[1];

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;

        if (this.tint) {
          context.fillStyle = this.tint.toCss();
          context.fillRect(0, 0, width, height);
          context.globalCompositeOperation = 'difference';
        }

        context.drawImage(image, 0, 0, width, height);
        return canvas;
      },
    );
  },
});
//  styling configuration
interface UniqueValue {
  value: string;
  color: string;
  linetype: string;
  linewidth: number;
  label: string;
  font_size: number;
}

if (!(window as any).__mvtFetchPatched) {
  const _fetch = window.fetch;
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === 'string' ? input
        : input instanceof URL ? input.href
          : (input as Request).url ?? '';
    let res = await _fetch(input, init);
    if (res.status === 202 && url.includes('/tiles/')) {
      let retries = 8;
      let delay = 800;
      while (retries-- > 0 && res.status === 202) {
        await new Promise((r) => setTimeout(r, delay));
        res = await _fetch(input, init);
        delay = Math.min(delay * 1.5, 6000);
      }
    }
    return res;
  };
  (window as any).__mvtFetchPatched = true;
}

const getMvtLineWidth = (linewidth: number): number => {
  const pixels = 3.779; // 1mm = 3.779 pixels
  let widthInPixels = 0.9447; // default (0.25mm)

  if (linewidth === 0) {
    widthInPixels = 0.13 * pixels;
  } else if (linewidth === -1 || linewidth === -2 || linewidth === -3) {
    widthInPixels = 0.9447;
  } else if (linewidth > 0) {
    widthInPixels = linewidth * 0.01 * pixels;
  }

  return widthInPixels * 2;
};

const buildMvtStyle = (siteId: string, vectorLayers: any[] = []) => {
  const dynamicLayers: any[] = [];

  vectorLayers.forEach((layer) => {
    const visibility = 'visible';
    const color = layer.color || '#FFFFFF';
    const lineWidth = getMvtLineWidth(layer.lineWidth ?? -1);

    // LINE entry — renders polyline geometries
    dynamicLayers.push({
      id: `${layer.id}-line`,
      type: 'line' as const,
      source: 'aw-source',
      'source-layer': layer.id,
      filter: ['==', ['geometry-type'], 'LineString'],
      layout: { visibility },
      paint: {
        'line-color': color,
        'line-width': lineWidth,
      },
    });

    // FILL entry — renders polygon geometries
    dynamicLayers.push({
      id: `${layer.id}-fill`,
      type: 'fill' as const,
      source: 'aw-source',
      'source-layer': layer.id,
      filter: ['==', ['geometry-type'], 'Polygon'],
      layout: { visibility },
      paint: {
        'fill-color': 'rgba(0,0,0,0)',
        'fill-outline-color': color,
      },
    });

    // CIRCLE entry — renders point geometries
    dynamicLayers.push({
      id: `${layer.id}-circle`,
      type: 'circle' as const,
      source: 'aw-source',
      'source-layer': layer.id,
      filter: ['==', ['geometry-type'], 'Point'],
      layout: { visibility },
      paint: {
        'circle-color': color,
        'circle-radius': 4,
        'circle-stroke-width': 1,
        'circle-stroke-color': color,
      },
    });

    // Text labels are rendered via a separate GeoJSONLayer (see text labels useEffect below)
    // ArcGIS VectorTileLayer does not render type: 'symbol' layers from custom MVT sources
  });

  return {
    version: 8 as const,
    sources: {
      'aw-source': {
        type: 'vector' as const,
        tiles: [`${API_URL}/tiles/${siteId}/{z}/{x}/{y}.mvt`],
        minzoom: 0,
        maxzoom: 22,
      },
    },
    layers: dynamicLayers,
  };
};

// Default camera target when an order has no location and no data is loaded
// yet — a US-wide view, since projects are US-based.
const US_DEFAULT_CENTER: [number, number] = [-98.5795, 39.8283];
const US_DEFAULT_ZOOM = 4;

// Rotating messages shown on the loading overlay while a 3D project loads
// (AUT-5999). They advance every LOADING_MESSAGE_INTERVAL_MS and hold on the
// last one if loading runs long.
const LOADING_MESSAGES = [
  'Loading your 3D project…',
  'Streaming point cloud data…',
  'Rendering high-resolution geometry…',
  'Optimizing the view for your scene…',
  'Almost ready—finalizing details…',
];
const LOADING_MESSAGE_INTERVAL_MS = 7000;
// Static subtext under the rotating message — sets expectations since large
// point clouds can take up to ~a minute to render.
const LOADING_SUBTEXT = 'Large point clouds can take up to a minute to load.';

const NAV_ARROW_LOCK_DEGREES = 45; // snap arrows to this grid: 8 evenly-spaced locks, no overlap
const MAX_VISIBLE_ARROWS = 6; // cap drawn arrows so junctions don't crowd (keyboard nav uses all)
const MAX_NEIGHBOR_DISTANCE_METERS = 50; // neighbour search radius per sector, true ground metres
const WEB_MERCATOR_RADIUS_METERS = 6378137; // rescales planar 3857 distances to true ground metres

// 8-point compass label for a world heading (0°=N, 90°=E) — shown next to the numeric view heading.
const COMPASS_8 = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
const compass8 = (deg: number): string => COMPASS_8[Math.round((((deg % 360) + 360) % 360) / 45) % 8];

// Arrow keys pick the nearest neighbour within KEYBOARD_MATCH_THRESHOLD of the view-relative
// direction (Up = ahead, then clockwise).
const KEY_OFFSET: Record<string, number | undefined> = {
  ArrowUp: 0, ArrowRight: 90, ArrowDown: 180, ArrowLeft: -90,
};
const KEYBOARD_MATCH_THRESHOLD = 60;

type PanoNeighbor = {
  feature: __esri.Graphic;
  sector: number; // 0..7, 45-degree bins from camera kappa
  relBearing: number; // bearing to the neighbour minus kappa, in (-180, 180]
  dist: number; // true ground metres; ranks arrows against MAX_VISIBLE_ARROWS
};

// A PanoNeighbor with a registered scene, drawn on the compass rose.
type NavArrow = PanoNeighbor & { sceneId: string };

const EsriSplitViewer: FC = () => {

  const params = useParams();

  const lasRef = useRef<HTMLDivElement | null>(null);
  const panoRef = useRef<HTMLDivElement | null>(null);
  const coneGraphicRef = useRef(null);
  const centerPointRef = useRef<__esri.Point | null>(null);
  const featureKappaRef = useRef<number>(0);
  // Yaw (deg) the next pano opens at, so it faces the same world direction (AUT-6181).
  const initialYawRef = useRef<number>(0);
  const [lasVisible, setLasVisible] = useState(true);
  const [panoVisible, setPanoVisible] = useState(false);
  const [panoControlsEnabled, setPanoControlsEnabled] = useState(false);
  // Opt-in pano vision: when the in-viewer toggle is on, the chatbot attaches a crop of the CURRENT pano
  // view to questions. Off by default. panoBitmapRef caches the fetched equirect so the crop is synchronous.
  const [panoVisionEnabled, setPanoVisionEnabled] = useState(false);
  const panoBitmapRef = useRef<ImageBitmap | null>(null);
  // Live center heading of the red view-cone (0°=N, 90°=E) = pano kappa + pannellum yaw. Surfaced on the
  // pano so a "pan ~N°" instruction is actionable — rotate until this readout reaches the target heading.
  const [viewHeadingDeg, setViewHeadingDeg] = useState<number | null>(null);
  const lasViewRef = useRef<__esri.SceneView | null>(null);
  const geoLayerRef = useRef<__esri.GeoJSONLayer | null>(null);
  const panoLayerRef = useRef<__esri.GeoJSONLayer | null>(null);
  const mapInstanceRef = useRef<__esri.Map | null>(null);
  const lasMapInstanceRef = useRef<__esri.Map | null>(null);
  const [featureList, setFeatureList] = useState<UniqueValue[]>([]);
  const featureListRef = useRef<UniqueValue[]>([]);
  const [checkedItems, setCheckedItems] = useState<{ [key: string]: boolean }>({});
  const [rasterLayers, setRasterLayers] = useState<any[]>([]);
  const [pointCloudLayers, setPointCloudLayers] = useState<PointCloudLayer[]>([]);
  const [mvtLayer, setMvtLayer] = useState<VectorTileLayer | null>(null);
  const textLabelsLayerRef = useRef<__esri.GeoJSONLayer | null>(null);
  const [featureLayers, setFeatureLayers] = useState<FeatureLayer[]>([]);
  const [activeCadId, setActiveCadId] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const { loading, loadFailure, load: loadProject } = useViewerProjectLoad(params.id);
  // True once the SceneView has actually rendered. `loading` only covers the
  // data thunks; the view paints a moment later, so we keep the loading overlay
  // up until viewReady to avoid a blank white screen in that gap.
  const [viewReady, setViewReady] = useState(false);
  // Counts Retries after a failed load. The failure page unmounts the map container, so each Retry mounts a new
  // one and the scene is built again into it (the scene effect below depends on this).
  const [sceneGeneration, setSceneGeneration] = useState(0);
  const retryLoad = () => {
    setSceneGeneration((generation) => generation + 1);
    setViewReady(false);
    loadProject();
  };
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const { status: tokenStatus, retry: retryToken } = useArcgisToken();
  const tokenRegistered = tokenStatus === 'registered';
  const [showPanoLayer, setShowPanoLayer] = useState(true);
  const views: (SceneView | MapView)[] = [];
  const [showPointCloud, setShowPointCloud] = useState(false);
  const [showClassifiedPointCloud, setShowClassifiedPointCloud] = useState({});
  const [resetZoom, setResetZoom] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const viewportCenter = useSelector((state: RootState) => state.map.common.viewport);

  const [popupVisible, SetPopupVisibility] = useState(false);
  const [distanceWidget3d, setDistanceWidget3d] = useState(null);
  const [AreaWidget3d, setAreaWidget3d] = useState(null);
  const [classVisibility, setClassVisibility] = useState<{ [fileTitle: string]: { [classId: string]: boolean } }>({});
  const [classesPresentByFile, setClassesPresentByFile] = useState<{ [fileTitle: string]: number[] | null }>({});
  const pointCloudLayersRef = useRef<PointCloudLayer[]>([]);
  const panoLayerFeatureRef = useRef<__esri.FeatureLayer | null>(null);
  const point3DLayerRef = useRef<__esri.FeatureLayer | null>(null);
  const currentLayersRef = useRef(null);
  const terrainRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [panoError, setPanoError] = useState<string | null>(null);
  const [panoWarning, setPanoWarning] = useState<string | null>(null);
  const [panoErrorClosed, setPanoErrorClosed] = useState(false);
  // Up to 8 navigation neighbors (nearest pano per 45-degree sector), selected
  // client-side in updateNavArrows; drives the compass arrows and keyboard nav.
  const panoNeighborsRef = useRef<PanoNeighbor[]>([]);
  // The nearest MAX_VISIBLE_ARROWS of those, each with a registered scene. Rendered as
  // the compass rose; the rAF loop below owns their transforms.
  const [navArrows, setNavArrows] = useState<NavArrow[]>([]);
  const compassRef = useRef<HTMLDivElement | null>(null);
  // ADJUST PANEL STATE (inside viewer)
  const [adjustPanelOpen, setAdjustPanelOpen] = useState<'classified' | 'output' | 'input' | null>(null);
  const [classifiedPanelOpen, setClassifiedPanelOpen] = useState(false);
  const [outputPanelOpen, setOutputPanelOpen] = useState(false);
  const [inputPanelOpen, setInputPanelOpen] = useState(false);
  const inputSliderRef = useRef<Slider | null>(null);
  const outputSliderRef = useRef<Slider | null>(null);
  const classSliderRef = useRef<Slider | null>(null);
  const [geodatabaseLayers, setGeodatabaseLayers] = useState<any[]>([]);
  const [shapefileLayers, setShapefileLayers] = useState<any[]>([]);
  const geodatabaseLayersRef = useRef<any[]>([]);
  const shapefileLayersRef = useRef<any[]>([]);

  // Get feature collection from Redux
  const featureCollection = useSelector(getFeatureCollection);
  const [geoLayer, setGeoLayer] = useState<GeoJSONLayer | null>(null);
  // Sidebar "KML Boundary" checkbox (AUT-6431) and the AOI draw tool both gate the boundary
  // layer; visibility is derived from both in one effect so the two controls never fight.
  const [kmlBoundaryVisible, setKmlBoundaryVisible] = useState(true);
  const [aoiToolActive, setAoiToolActive] = useState(false);
  const toggleKmlBoundaryVisibility = () => setKmlBoundaryVisible((prev) => !prev);
  const rasterTileJson = useSelector((state: RootState) => state.map.common.rasterTileJson);
  const projectData = useAppSelector((state) => state.project.project);
  // One mount gate for every Khora companion surface in the viewer (pano chip + looking-angle
  // readout, gdb-rows table, region chip, output Dashboard) — identical to the launcher's own
  // gate, so a non-pilot project (or ?khora=off) shows NO Khora UI at all.
  const khoraUiEnabled = isKhoraUiEnabled(projectData?.name);
  const authToken = useAppSelector((state) => state.auth.token);
  const orders = useSelector((state: RootState) => state.order.list);
  // Declared here rather than with the other order state below because projectGeoidOffset and the
  // geoid-status effect need them, and `const` is not hoisted.
  const selectedOrderId = useAppSelector((state) => state.map.editor.present.orderSelected);
  const placedOrders = useAppSelector((state) => getOrders(state).placedOrders);
  const draftOrder = useAppSelector((state) => getOrders(state).activeOrder);
  const visibleCount = [lasVisible, panoVisible].filter(Boolean).length;
  const visibleCountStyle = Object.values(visibleCount).filter((v) => v).length;
  const viewWidth = visibleCount > 0 ? `${100 / visibleCount}%` : '100%';
  const styles = getStyles(viewWidth, { las: lasVisible, pano: panoVisible }, visibleCount, lasVisible, panoVisible);
  const panoFeatures = useSelector((state: RootState) => state.esri.pano.features);
  const AnnotationFeatures = useAppSelector((state) => state.esri.annotation.list);
  const [imageSource, setImageSource] = useState<string | null>(null);
  const currentFeatureRef = useRef<__esri.Graphic | null>(null);
  const currentSceneIdRef = useRef<string>('firstScene');
  const [failedPanoIds, setFailedPanoIds] = useState<string[]>([]);
  const style = stylesDef();
  const dispatch = useAppDispatch();

  // Geoid correction (metres) for the SELECTED order. Published data is NAVD88-orthometric, which
  // sits ~one geoid separation off the WGS84 terrain. Instead of lifting every data layer, we shift
  // the ground itself (see OffsetElevationLayer) by -geoidOffsetZ so native-Z data renders flush.
  // Read from the selected order specifically: `state.order.list` is not project-filtered, so
  // matching on "the first order that has a value" could apply a different order's correction.
  const projectGeoidOffset = useMemo(
    () => resolveOrderGeoidOffset(orders, selectedOrderId),
    [orders, selectedOrderId],
  );
  // What the draft order can say about its automatic offset (no KML yet, none found, or ready),
  // shown read-only before anything is placed. It never feeds projectGeoidOffset: the ground is not
  // shifted until the order is placed.
  const pendingGeoidState = useMemo(() => resolvePendingGeoidState(draftOrder), [draftOrder]);

  // An order can carry no correction at all — the lookup failed at KML-upload time, its boundary
  // predates the feature, or it is a corridor. Ask the server to compute one, once per order; it
  // never overwrites a stored value, so a manual correction always wins.
  // Starts 'resolving', not 'resolved': the effect below hasn't run yet on first render (it bails
  // until selectedOrderId/orders are populated), so defaulting to 'resolved' would have the 'i'
  // tooltip and status line claim a correction is already in force before anything was checked.
  const [geoidStatus, setGeoidStatus] = useState<GeoidStatus>('resolving');
  // Why geoidStatus is 'unavailable' — a genuine failure (worth retrying) vs. this order simply
  // having no correction to find (expected). Drives the sidebar's message; never left unexplained.
  const [geoidUnavailableReason, setGeoidUnavailableReason] = useState<EnsureGeoidOffsetReason | null>(null);
  const ensuredGeoidOrderRef = useRef<string | null>(null);
  useEffect(() => {
    // Nothing placed yet: whatever correction the draft carries was computed at KML upload and is
    // only shown, never applied. No ensure call either; a draft that has none self-heals on the
    // first open once the order is placed.
    if (placedOrders.length === 0) {
      setGeoidStatus('pending');
      setGeoidUnavailableReason(null);
      return;
    }
    if (!selectedOrderId || !Array.isArray(orders)) return;
    const order = orders.find((o) => o._id === selectedOrderId);
    if (!order) return;
    // Negative values are legitimate wherever the geoid sits above the ellipsoid (Australia, for
    // one). Exactly 0 is the placeholder for "none was calculated" and is shown as such without a
    // lookup: a stored 0 is deliberate and never recomputed over. Only a missing value asks the
    // server.
    const resolution = resolvePlacedGeoidResolution(order.geoidOffsetZ);
    if (resolution !== 'ask-server') {
      setGeoidStatus(resolution === 'resolved' ? 'resolved' : 'unavailable');
      setGeoidUnavailableReason(null);
      return;
    }
    if (ensuredGeoidOrderRef.current === selectedOrderId) return;
    ensuredGeoidOrderRef.current = selectedOrderId;
    setGeoidStatus('resolving');
    // A failure here is not fatal: the sidebar says so (with why) and the user can still nudge by
    // hand. EnsureGeoidOffsetThunk already logs the failure — this only drives the UI message.
    dispatch(EnsureGeoidOffsetThunk(selectedOrderId)).then(({ applied, reason }: EnsureGeoidOffsetResult) => {
      setGeoidStatus(applied ? 'resolved' : 'unavailable');
      setGeoidUnavailableReason(applied ? null : reason ?? null);
    });
  }, [selectedOrderId, orders, placedOrders, dispatch]);

  // The user's own vertical nudge (metres) for the loaded geodatabase — the sidebar's
  // "Elevation offset" field, always in metres regardless of its display-unit toggle. A DELTA on
  // top of the automatic geoid correction above, so 0 means "sit on the geoid plane" rather than
  // "no correction at all"; the correction itself is shown beside the field as read-only context.
  // Saved per geodatabase once edited.
  const [userElevationOffset, setUserElevationOffset] = useState<number>(0);

  // Single shift applied to the ground (see OffsetElevationLayer). Lowering the terrain by
  // (geoid + nudge) lifts every native-Z data layer by the same amount at once.
  const groundOffset = useMemo(
    () => resolveGroundOffset(projectGeoidOffset, userElevationOffset),
    [projectGeoidOffset, userElevationOffset],
  );

  // Rebuild the ground whenever the offset changes — orders load after the
  // scene is created, and the offset changes on demand. Recreating forces the
  // elevation tiles to re-fetch; debounced so rapid input doesn't thrash the terrain.
  useEffect(() => {
    const map = lasMapInstanceRef.current;
    if (!map) return undefined;
    const timer = setTimeout(() => {
      map.ground = new Ground({ layers: [new OffsetElevationLayer({ offset: groundOffset })] });
    }, 350);
    return () => clearTimeout(timer);
  }, [groundOffset]);

  const { id } = useParams();
  const [popupData, setPopupData] = useState(null);
  const [popupIndex, setPopupIndex] = useState(0);
  const [filteredFeatures, setFilteredFeatures] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  // Shared "selected feature": the popup click handler (single source) sets it; the dashboard
  // layer-focus and the cinematic orbit both react to it — no separate per-feature listeners.
  const [selectedFeature, setSelectedFeature] = useState<__esri.Graphic | null>(null);
  const [MeasuringDistance, SetMeasurmentActiveDis] = useState(false);
  const [MeasuringArea, SetMeasurmentActiveArea] = useState(false);
  const measuringRefDistance = useRef(MeasuringDistance);
  const measuringRefArea = useRef(MeasuringArea);
  const [popupXY, setPopupXY] = useState<{ x: number; y: number } | null>(null);
  const [lasViewref, setlasView] = useState(null);
  const islasViewRef = useRef(false);
  // Khora "Dashboard layers" (add_spatial_layer): the viewerLayers slice is the channel from the chat
  // (separate React tree). This ref tracks the GeoJSONLayers this component added to the scene
  // (id → layer + its blob URL) so the sync effect can dedupe/remove them and the click handler can
  // hit-test them for per-feature popups. NOT wired through CinematicToolbar (per the design).
  const viewerLayers = useAppSelector((state) => state.viewerLayers.layers);
  // Khora per-feature analysis (add_feature_analysis) to inject into EXISTING feature popups, keyed by
  // layer name. A ref so the click handler's buildFeatureDisplay reads the latest without re-binding.
  const featureAnalysis = useAppSelector((state) => state.dashboard?.featureAnalysis ?? {});
  const featureAnalysisRef = useRef(featureAnalysis);
  featureAnalysisRef.current = featureAnalysis;
  // Plain object (id → tracked layer), NOT a Map — `Map` is shadowed by Esri's @arcgis/core/Map in
  // this file, so `new Map()` here would build an Esri map widget, not a JS map. `signature` lets the
  // sync effect detect when a re-emitted same-id layer's DATA changed (rebuild) vs just visibility.
  const khoraLayersRef = useRef<Record<string, { layer: GeoJSONLayer; blobUrl?: string; signature: string }>>({});
  // Identity of the map the tracked layers belong to; when the SceneView is rebuilt (new map), the
  // tracker is reset so layers re-add to the new map instead of being orphaned on the destroyed one.
  const khoraMapRef = useRef<unknown>(null);

  // Render the features Khora surfaced via show_on_map / a chart-click highlight (setChatHighlight).
  // Relocated here from the removed CinematicToolbar so the chat→map highlight keeps working.
  const chatHighlight = useAppSelector((state) => state.dashboard.chatHighlight);
  const chatQueryResult = useAppSelector((state) => state.dashboard.chatQueryResult);
  const loadedGdbId = useAppSelector((state) => state.dashboard.loadedGdbId);
  // Khora highlights the ALREADY-DISPLAYED features in place: the highlighter resolves each target
  // layer in the loaded geodatabase (geodatabaseLayersRef) and highlights the matched features at
  // their REAL geometry — so the app owns feature display (heights/elevation) and Khora only outlines.
  // (No feet->meters Z-conversion: the queried geometry is the layer's own, already at true elevation.)
  useEffect(() => {
    if (!KHORA_ENABLED) return; // master kill switch — no chat→map highlight work when Khora is off
    if (lasViewref && (chatHighlight?.targets?.length || chatHighlight?.geojson?.features?.length)) {
      showChatHighlight(lasViewref, chatHighlight, geodatabaseLayersRef.current);
    } else if (lasViewref) {
      // Highlight cleared (e.g. an order switch nulls chatHighlight) — remove any live overlay in place
      // instead of leaving it until the view is rebuilt.
      clearChatHighlight(lasViewref);
    }
  }, [lasViewref, chatHighlight]);
  useEffect(() => () => clearChatHighlight(lasViewref), [lasViewref]);

  // Khora go_to_pano: open the nearest pano to a pole (or a named pano) and orient the 360 toward it.
  // Mirrors the chatHighlight bridge above — EsriSplitViewer owns the pano viewer, so the command is
  // consumed here. Fires only when a new command arrives (chatPano changes).
  const chatPano = useAppSelector((state) => state.dashboard.chatPano);
  useEffect(() => {
    if (!KHORA_ENABLED || !chatPano) return undefined;
    const panoLayer = panoLayerFeatureRef.current;
    if (!panoLayer) return undefined; // pano layer not built yet (no panos for this project / still loading)
    let cancelled = false;
    (async () => {
      const resolved = await resolvePanoTarget(panoLayer, chatPano);
      if (cancelled) return;
      if (!resolved) {
        console.warn('go_to_pano: no panorama found near', chatPano.lng, chatPano.lat);
        return;
      }
      setPanoVisible(true);
      await loadPanoScene(resolved.feature, panoLayer, 'firstScene', false, resolved.worldHeading);
    })();
    return () => { cancelled = true; };
    // Also retry when the pano layer becomes ready (panoControlsEnabled flips true as panoFeatures load):
    // a go_to_pano that arrived while panos were still loading would otherwise hit the null-ref guard above
    // and be silently dropped. Re-opening the same pano if it toggles later is a benign no-op re-navigation.
  }, [chatPano, panoControlsEnabled]);
  // Pano-vision bridge (viewer → chatbot). While the toggle is on and a pano is loaded: mark active (drives
  // the chatbot hint), fetch the equirect ONCE (best-effort, CORS-gated) into panoBitmapRef, and register a
  // synchronous capturer that crops the CURRENT yaw/pitch/hfov at SEND time. All cleared on off/close/unmount.
  useEffect(() => {
    const active = Boolean(panoVisionEnabled && panoVisible && imageSource);
    dispatch(setPanoVisionActive(active));
    if (!active) {
      registerPanoCapturer(null);
      panoBitmapRef.current?.close?.();
      panoBitmapRef.current = null;
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(imageSource as string);
        if (!response.ok || cancelled) return;
        const bitmap = await createImageBitmap(await response.blob());
        if (cancelled) { bitmap.close?.(); return; }
        panoBitmapRef.current?.close?.();
        panoBitmapRef.current = bitmap;
      } catch { /* pano vision stays dark if the source bytes can't be read (e.g. S3 CORS) */ }
    })();
    registerPanoCapturer(() => {
      const bitmap = panoBitmapRef.current;
      if (!bitmap) return null;
      const el = panoRef.current;
      const aspect = el && el.clientHeight ? el.clientWidth / el.clientHeight : 1.5;
      const yaw = ReactPannellum.getYaw?.() ?? 0;
      const hfov = ReactPannellum.getHfov?.() ?? 100;
      const image = cropEquirectToView(
        bitmap, yaw, ReactPannellum.getPitch?.() ?? 0, hfov, aspect);
      if (!image) return null;
      // Ground the frame to a pole: the camera's WGS84 location + the world heading the view faces
      // (kappa + yaw, same as the on-map cone). Khora resolves the pole this ray covers, so it
      // cross-checks the RIGHT record instead of matching attachment counts to the photo.
      const panoPoint = currentFeatureRef.current?.geometry as __esri.Point | undefined;
      const view = (panoPoint && Number.isFinite(panoPoint.longitude) && Number.isFinite(panoPoint.latitude))
        ? {
          lon: panoPoint.longitude,
          lat: panoPoint.latitude,
          bearing_deg: ((((featureKappaRef.current ?? 0) + yaw) % 360) + 360) % 360,
          hfov_deg: hfov,
        }
        : null;
      return { image, view };
    });
    return () => {
      cancelled = true;
      dispatch(setPanoVisionActive(false));
      registerPanoCapturer(null);
      panoBitmapRef.current?.close?.();
      panoBitmapRef.current = null;
    };
  }, [panoVisionEnabled, panoVisible, imageSource, dispatch]);
  const [isAdding, setIsAdding] = useState(false);
  const isAddingRef = useRef(isAdding);
  const isAddingRef3d = useRef(isAdding);
  const ifStart = useRef(false);
  const [AnnotationEdit, setAnnotationEdit] = useState(null);
  const annotationWidgetRef = useRef(null);
  const annotationWidgetRef3d = useRef(null);
  const [annotations, setAnnotations] = useState([]);
  const [annotations3d, setAnnotations3d] = useState([]);
  const existingAnnotaion = useRef([]);
  const newLabelRef = useRef(false);
  const basemapRef = useRef(null);
  const [homeWidget, setHomeWidget] = useState<any>(null);
  const [aoiWidget, setAoiWidget] = useState<any>(null);
  const [overlaySize, setOverlaySize] = useState({ width: 0, height: 0 });
  const [overlaySize3d, setOverlaySize3d] = useState({ width: 0, height: 0 });
  const [overlayposition, setOverlayPosition] = useState({ top: 0, left: 0 });
  const [overlayposition3d, setOverlayPosition3d] = useState({ top: 0, left: 0 });
  const [orderid, setorderid] = useState(null);
  const orderref = useRef(null);
  const initailExtentref = useRef(null);
  const lastKmlStringRef = useRef<string>('');
  const lastPanoStringRef = useRef<string>('');
  const lastRasterStringRef = useRef<string>('');
  const lastPointCloudStringRef = useRef<string>('');
  const lastMapUsedRef = useRef<__esri.Map | null>(null);
  // The raster effect needs its OWN "last map I added to" ref: sharing lastMapUsedRef with the pano effect
  // (which runs first and sets it to the new map) defeated raster's map-change guard, so raster tiles were
  // never re-added to a rebuilt scene.
  const lastRasterMapRef = useRef<__esri.Map | null>(null);
  const lastOrderIdRef = useRef<string | null>(null);
  const lastPointCloudMapRef = useRef<__esri.Map | null>(null);
  const otherwidgetactiveref = useRef(false);
  const mvtLayerRef = useRef<VectorTileLayer | null>(null);
  const mvtDestroyCountRef = useRef(0);
  const [mvtDestroyCount, setMvtDestroyCount] = useState(0);
  // Flatten all cadFiles from orders
  const cadFiles = orders?.flatMap((order: any) => order.cadFiles || []) || [];
  const [projectId, setProjectId] = useState<string | null>(null);
  const navigate = useNavigate();
  const vectorTileJson = useAppSelector((state) => state.map.common.vectorTileJson);
  const fileVersions = useAppSelector((state) => state.order.fileVersions);
  const sortedPlacedOrders = useMemo(
    () =>
      [...placedOrders].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      }),
    [placedOrders],
  );
  const orderIdList = sortedPlacedOrders.map((order) => order._id);
  // Stable primitive for the scene-construction effect below. sortedPlacedOrders is a NEW array
  // reference on every state.order.list change -- including an elevation-offset or geoid-offset
  // save, which mutate one field on one order -- even though the actual set of orders never moved.
  // Using the array itself as that effect's dependency tore down and rebuilt the whole Map/SceneView
  // on every such save, discarding the loaded GDB/shapefile layers; the separate effect that
  // (re)populates them only reacts to [selectedOrderId, tokenRegistered, !!lasViewref], none of which
  // change on a rebuild, so nothing ever reloaded them. Comparing order IDENTITY by id, not by object
  // reference, is what the effect actually needs.
  const orderIdsKey = orderIdList.join(',');
  const selectedOrder = sortedPlacedOrders.find((o) => o._id === selectedOrderId);
  // AUT-6678: where the viewer flies for each order, pulled inside its KML once that has loaded.
  const flightTargets = useFlightTargets(sortedPlacedOrders, featureCollection);
  const selectedFlightTarget = selectedOrderId ? flightTargets[selectedOrderId] : null;
  // Elevation offset persistence (per geodatabase, per order). The stored nudge rides in on the same
  // geoDbFiles payload that decides which gdb loads, so there is nothing to wait for and no race:
  // by the time loadedGdbId names a gdb, that gdb's saved nudge is already in Redux.
  const storedGdbElevation = useMemo((): number => {
    const geoDbFile = selectedOrder?.geoDbFiles?.find((f: any) => String(f._id) === loadedGdbId);
    return resolveUserElevationOffset(geoDbFile?.elevationOffsetZ);
  }, [selectedOrder, loadedGdbId]);

  // Hydrate once per geodatabase. Keyed on the gdb id rather than the value so a user's own edit
  // (which writes the value back to Redux) can't bounce back through here and undo their typing.
  const hydratedGdbRef = useRef<string | null>(null);
  useEffect(() => {
    if (!loadedGdbId || hydratedGdbRef.current === loadedGdbId) return;
    hydratedGdbRef.current = loadedGdbId;
    setUserElevationOffset(storedGdbElevation);
    setElevationSaveFailed(false);
  }, [loadedGdbId, storedGdbElevation]);

  // Whether the most recent save to the loaded gdb failed to reach the server. The terrain still
  // reflects the value the user typed (SaveGdbElevationThunk already logged the failure) — this
  // only drives a visible warning, since without it the edit silently does not persist: on the next
  // load the field reverts and nothing told the user why.
  const [elevationSaveFailed, setElevationSaveFailed] = useState(false);

  // Apply the nudge immediately (the terrain rebuild is debounced downstream) and persist it
  // against the loaded gdb.
  const commitUserElevationOffset = useCallback((value: number) => {
    setUserElevationOffset(value);
    setElevationSaveFailed(false);
    if (!loadedGdbId) return;
    dispatch(SaveGdbElevationThunk(loadedGdbId, value)).then((saved: boolean) => {
      if (!saved) setElevationSaveFailed(true);
    });
  }, [dispatch, loadedGdbId]);

  const cadIdsForOrder = (selectedOrder?.cadFiles ?? []).map((c: any) => c._id);
  const kmlFeaturesForOrder = (selectedOrder?.cadFiles ?? []).flatMap((c: any) => c.kmlFeatures ?? []);
  const [adjustPanelPosition, setAdjustPanelPosition] = useState<{ top: number; left: number } | null>(null);
  const [classifiedPointSize, setClassifiedPointSize] = useState<number>(2);
  const [outputPointSize, setOutputPointSize] = useState<number>(10);
  const [inputPointSize, setInputPointSize] = useState<number>(2);

  const getLasTitle = useCallback((las: any): string => {
    const fullPath = las.filename || '';
    const index = projectData?.lasFiles ? projectData.lasFiles.indexOf(las) : 0;
    const filename =
      fullPath.substring(fullPath.lastIndexOf('/') + 1) ||
      las.lasFilename ||
      `PointCloud-${index >= 0 ? index : 0}`;
    return filename.replace(/\.(las|laz)$/, '');
  }, [projectData?.lasFiles]);

  const openAdjustPanel = useCallback((panelType: 'classified' | 'output' | 'input') => {
    // Reset position when switching panels
    setAdjustPanelPosition(null);

    switch (panelType) {
      case 'classified':
        setClassifiedPanelOpen((prev) => !prev);
        break;
      case 'output':
        setOutputPanelOpen((prev) => !prev);
        break;
      case 'input':
        setInputPanelOpen((prev) => !prev);
        break;
    }
  }, []);

  // Persist each placed order's flight target (inside its KML once that has loaded, AUT-6678) so a later
  // mount can restore that order's extent. Runs as a
  // side-effect keyed on the placed-order set — NOT in the render body: writing localStorage during
  // render is an impure render that re-serializes + rewrites storage on every re-render (typing, hover,
  // panel toggles). Re-runs only when the orders or their resolved targets actually change.
  useEffect(() => {
    const extentMap: Record<string, unknown> = {};
    sortedPlacedOrders.forEach((order) => {
      const target = flightTargets[order._id];
      if (target) extentMap[order._id] = { latitude: target.latitude, longitude: target.longitude };
      else if (order?.middlePoint) extentMap[order._id] = order.middlePoint;
    });
    localStorage.setItem('extent', JSON.stringify(extentMap));
  }, [sortedPlacedOrders, flightTargets]);
  const getAdjustPanelStyles = (baseStyles: any) => ({
    ...baseStyles,
    position: 'fixed' as const,
    top: adjustPanelPosition ? `${adjustPanelPosition.top + 8}px` : '50%',
    left: adjustPanelPosition ? `${adjustPanelPosition.left}px` : '50%',
    transform: adjustPanelPosition ? 'none' : 'translate(-50%, -50%)',
    zIndex: 10000,
  });

  const initialLasStatuses = useMemo(() => {
    if (!projectData?.lasFiles || !Array.isArray(projectData?.lasFiles)) {
      return {};
    }
    return projectData?.lasFiles.reduce((acc, file) => {
      const title = getLasTitle(file);
      acc[title] = file.lasType === 'unclassified'
        ? false
        : file.ownerOrder === selectedOrderId; // Default: classified visible for selected order; unclassified OFF
      return acc;
    }, {} as { [key: string]: boolean });
  }, [projectData?.lasFiles, getLasTitle, selectedOrderId]);

  const [lasFilesDisplayStatuses, setLasFilesDisplayStatuses] = useState<{ [key: string]: boolean }>(initialLasStatuses);

  useEffect(() => {
    const lasMap = lasMapInstanceRef.current;
    if (!lasMap) return;

    if (mvtLayerRef.current) {
      lasMap.remove(mvtLayerRef.current);
      mvtLayerRef.current.destroy();
      mvtLayerRef.current = null;
      setMvtLayer(null);
      (window as any).mvtLayer = null;
      (window as any).mvtLayerStyle = null;
    }
    if (textLabelsLayerRef.current) {
      lasMap.remove(textLabelsLayerRef.current);
      textLabelsLayerRef.current.destroy();
      textLabelsLayerRef.current = null;
      (window as any).textLabelsLayer = null;
    }
    mvtDestroyCountRef.current += 1;
    setMvtDestroyCount((c) => c + 1);
  }, [selectedOrderId]);

  //mvt use effect 
  useEffect(() => {
    if (!vectorTileJson || !selectedOrderId) return;
    const lasMap = lasMapInstanceRef.current;
    if (!lasMap) return;
    const activeSiteId = fileVersions?.[selectedOrderId];
    if (!activeSiteId) return;

    const getVectorLayersForMvt = (): any[] => {
      const cadMatch = vectorTileJson?.[selectedOrderId]?.[activeSiteId];
      if (cadMatch?.vector_layers?.length) {
        return cadMatch.vector_layers;
      }
      const orderVtj = vectorTileJson?.[selectedOrderId];
      if (orderVtj) {
        for (const fileVersion of Object.keys(orderVtj)) {
          const vtj = orderVtj[fileVersion];
          if (vtj?.vector_layers?.length) return vtj.vector_layers;
        }
      }
      return [];
    };

    const vectorLayers = getVectorLayersForMvt();
    if (!vectorLayers.length) return;

    // Destroy any leftover layer
    if (mvtLayerRef.current) {
      lasMap.remove(mvtLayerRef.current);
      mvtLayerRef.current.destroy();
      mvtLayerRef.current = null;
    }

    const updatedStyle = buildMvtStyle(activeSiteId, vectorLayers);
    const newMvtLayer = new VectorTileLayer({
      style: updatedStyle as any,
      title: 'MVT Features',
    });

    lasMap.add(newMvtLayer);
    mvtLayerRef.current = newMvtLayer;
    setMvtLayer(newMvtLayer);
    (window as any).mvtLayer = newMvtLayer;
    (window as any).mvtLayerStyle = updatedStyle;

    // ── KEY FIX: re-apply the current CadDrawings visibility state ──────────
    // After a rebuild the new layer shows everything visible. If the user had
    // toggled some layers off before switching orders and back, re-apply.
    const undergroundCheckedItems = (window as any).undergroundCheckedItems as Record<string, boolean> | undefined;
    if (undergroundCheckedItems && Object.keys(undergroundCheckedItems).length > 0) {
      const visibleIds = Object.keys(undergroundCheckedItems).filter((id) => undergroundCheckedItems[id]);
      const currentStyle = newMvtLayer.style as any;
      if (currentStyle?.layers) {
        const patched = currentStyle.layers.map((sl: any) => {
          const baseId = sl.id
            .replace(/-line$/, '')
            .replace(/-fill$/, '')
            .replace(/-circle$/, '');
          return {
            ...sl,
            layout: {
              ...sl.layout,
              visibility: visibleIds.includes(baseId) ? 'visible' : 'none',
            },
          };
        });
        newMvtLayer.loadStyle({ ...currentStyle, layers: patched }).catch(console.error);
      }
    }

  }, [vectorTileJson, selectedOrderId, fileVersions, mvtDestroyCount]);

  // Fetch text labels as GeoJSON and render via GeoJSONLayer with LabelClass
  // (ArcGIS VectorTileLayer does not render type: 'symbol' layers from custom MVT sources)
  useEffect(() => {
    if (!vectorTileJson || !selectedOrderId) return;
    const lasMap = lasMapInstanceRef.current;
    if (!lasMap) return;
    const activeSiteId = fileVersions?.[selectedOrderId];
    if (!activeSiteId) return;

    let blobUrl: string | null = null;
    const abortController = new AbortController();

    const getTextLabelsDefinitionExpression = () => {
      const items = (window as any).undergroundCheckedItems as Record<string, boolean> | undefined;
      if (!items) return '1=0';
      const visibleNames = Object.keys(items).filter((id) => items[id]);
      if (visibleNames.length === 0) return '1=0';
      return `layer_name IN (${visibleNames.map((n) => `'${n.replace(/'/g, "''")}'`).join(',')})`;
    };

    const applyTextLabelsFilter = () => {
      if (textLabelsLayerRef.current) {
        textLabelsLayerRef.current.definitionExpression = getTextLabelsDefinitionExpression();
      }
    };

    const removeTextLabels = () => {
      if (textLabelsLayerRef.current) {
        lasMap.remove(textLabelsLayerRef.current);
        textLabelsLayerRef.current.destroy();
        textLabelsLayerRef.current = null;
        (window as any).textLabelsLayer = null;
      }
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        blobUrl = null;
      }
    };

    // Clean up previous text labels layer
    removeTextLabels();

    const loadTextLabels = async () => {
      try {
        const response = await fetch(`${API_URL}/tiles/${activeSiteId}/getTextLabels`, {
          headers: { Authorization: `Bearer ${authToken}` },
          signal: abortController.signal,
        });
        if (!response.ok) return;
        const geojson = await response.json();
        if (!geojson?.features?.length) return;

        // Check if this effect has been cleaned up (request was aborted)
        if (abortController.signal.aborted) return;

        const blob = new Blob([JSON.stringify(geojson)], { type: 'application/json' });
        blobUrl = URL.createObjectURL(blob);

        // Create one LabelClass per unique color
        const uniqueColors = [...new Set(geojson.features.map((f: any) => f.properties.color))];
        const labelClasses = uniqueColors.map((color: string) =>
          new LabelClass({
            labelExpressionInfo: { expression: '$feature.text' },
            where: `color = '${color}'`,
            symbol: {
              type: 'text',
              color: color || '#FFFFFF',
              font: { size: 10, family: 'Arial Unicode MS' },
            } as any,
            minScale: 4500,
            maxScale: 0,
          }),
        );

        const textLayer = new GeoJSONLayer({
          url: blobUrl,
          title: 'Text Labels',
          renderer: {
            type: 'simple',
            symbol: {
              type: 'simple-marker',
              size: 0,
              color: [0, 0, 0, 0],
              outline: { width: 0, color: [0, 0, 0, 0] },
            },
          } as any,
          labelingInfo: labelClasses,
          definitionExpression: getTextLabelsDefinitionExpression(),
          visible: true,
        });

        // Double-check ref hasn't changed (another effect cleanup may have fired)
        if (!textLabelsLayerRef.current || textLabelsLayerRef.current.id !== (textLayer as any).id) {
          lasMap.add(textLayer);
          textLabelsLayerRef.current = textLayer;
          (window as any).textLabelsLayer = textLayer;
          (window as any).lasMapRef = lasMapInstanceRef.current;
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Request was cancelled, this is expected
          return;
        }
        console.error('Failed to load 2D text labels', err);
      }
    };

    (window as any).loadTextLabels = loadTextLabels;
    (window as any).removeTextLabels = removeTextLabels;
    (window as any).applyTextLabelsFilter = applyTextLabelsFilter;

    // Only auto-fetch if 2D has any visible layer. CadDrawings's master/sub toggles
    // call window.loadTextLabels / window.applyTextLabelsFilter / window.removeTextLabels.
    const undergroundCheckedItems = (window as any).undergroundCheckedItems as Record<string, boolean> | undefined;
    const has2DVisible = !!undergroundCheckedItems && Object.values(undergroundCheckedItems).some(Boolean);
    if (has2DVisible) {
      loadTextLabels();
    }

    return () => {
      abortController.abort();
      removeTextLabels();
      (window as any).loadTextLabels = null;
      (window as any).removeTextLabels = null;
      (window as any).applyTextLabelsFilter = null;
    };
  }, [vectorTileJson, selectedOrderId, fileVersions]);

  useEffect(() => { featureListRef.current = featureList; }, [featureList]);

  useEffect(() => {
    pointCloudLayersRef.current = pointCloudLayers;
  }, [pointCloudLayers]);

  // All-visible visibility map keyed exactly like the legend rows for this file:
  // present classes when statistics are available, the named list otherwise.
  const buildAllVisibleClassMap = (lasType: string, newClasses: boolean | undefined, presentIds: number[] | null) => {
    const classMap: { [classId: string]: boolean } = {};
    getLegendClasses(lasType, newClasses, presentIds).classes.forEach((cls) => {
      classMap[String(cls.classId)] = true;
    });
    classMap['-1'] = true;
    return classMap;
  };

  // Read each classified layer's cached CLASS_CODE statistics to learn which
  // classes are actually present; null means unavailable and the legend falls
  // back to the full named list.
  useEffect(() => {
    let cancelled = false;
    pointCloudLayers.forEach((layer) => {
      const { lasType } = layer as PointCloudLayer & { lasType?: string };
      const { title } = layer;
      if (lasType !== 'all_classes' || !title) return;
      layer.load()
        .then(() => layer.queryCachedStatistics('CLASS_CODE'))
        .then((statsResponse: unknown) => {
          if (cancelled) return;
          const presentIds = parsePresentClassIds(statsResponse);
          if (!presentIds) {
            console.warn(`No parseable CLASS_CODE statistics for ${title}; legend falls back to the named class list.`, statsResponse);
          }
          setClassesPresentByFile((prev) => ({ ...prev, [title]: presentIds }));
          if (presentIds) {
            const lasFile = projectData?.lasFiles?.find((f: any) => getLasTitle(f) === title);
            setClassVisibility((prev) => ({
              ...prev,
              [title]: buildAllVisibleClassMap(lasFile?.lasType, lasFile?.newClasses, presentIds),
            }));
          }
        })
        .catch((err) => {
          if (cancelled) return;
          console.warn(`Failed to load CLASS_CODE statistics for ${title}:`, err);
          setClassesPresentByFile((prev) => ({ ...prev, [title]: null }));
        });
    });
    return () => { cancelled = true; };
  }, [pointCloudLayers]);

  useEffect(() => {
    if (!pointCloudLayers.length || !selectedOrderId) return;

    pointCloudLayers.forEach((layer) => {
      const lasFile = projectData?.lasFiles?.find((f: any) => getLasTitle(f) === layer.title);
      if (!lasFile || lasFile.lasType === 'unclassified') return;

      setClassVisibility((prev) => {
        // Only initialize
        const existing = prev[layer.title];

        // if '-1' key is absent
        if (existing && Object.keys(existing).length > 0 && '-1' in existing) return prev;
        const classMap = buildAllVisibleClassMap(lasFile.lasType, lasFile.newClasses, classesPresentByFile[String(layer.title)] ?? null);
        if (existing && Object.keys(existing).length > 0) {
          return { ...prev, [layer.title]: { ...existing, '-1': true } };
        }
        return { ...prev, [layer.title]: classMap };
      });
    });
  }, [pointCloudLayers, selectedOrderId]);

  useEffect(() => {
    if (!selectedOrderId || !projectData?.lasFiles) return;

    setClassVisibility((prev) => {
      const next = { ...prev };

      projectData.lasFiles.forEach((file: any) => {
        if (file.ownerOrder !== selectedOrderId) return;
        if (file.lasType === 'unclassified') return;

        const title = getLasTitle(file);

        // Always rebuild for the incoming order's layers
        next[title] = buildAllVisibleClassMap(file.lasType, file.newClasses, classesPresentByFile[title] ?? null);
      });

      return next;
    });
  }, [selectedOrderId]);

  useEffect(() => {
    setLasFilesDisplayStatuses((prev) => {
      const merged: { [key: string]: boolean } = { ...initialLasStatuses };
      // Only carry over previous user choices for keys that still exist in the new order
      Object.keys(initialLasStatuses).forEach((key) => {
        if (key in prev) merged[key] = prev[key];
      });
      return merged;
    });
  }, [initialLasStatuses]);

  const createPointCloudLayers = useCallback(() => {
    try {
      if (!projectData?.lasFiles || !Array.isArray(projectData?.lasFiles)) {
        console.warn('No lasFiles found in project data.');
        setShowPointCloud(false);
        return [];
      }

      const validLasFiles = projectData?.lasFiles.filter((file: any) => {
        const hasUrl = !!file.esriUrl;
        const hasNoError = !file.esriError || file.esriError.toLowerCase() === 'success';

        if (!hasUrl) {
          console.warn(`Skipping LAS file ${file.filename}: No esriUrl`);
        }
        if (!hasNoError) {
          console.warn(`Skipping LAS file ${file.filename}: Error - ${file.esriError}`);
        }

        return hasUrl && hasNoError;
      });

      if (!validLasFiles.length) {
        console.warn('No valid LAS files found.');
        setShowPointCloud(false);
        return [];
      }

      const latestOutputByOrder: { [orderId: string]: any } = {};
      projectData?.lasFiles
        .filter((f: any) => f.ownerOrder)
        .forEach((f: any) => {
          const orderId = f.ownerOrder;
          if (!latestOutputByOrder[orderId]) {
            latestOutputByOrder[orderId] = f;
          } else {
            const prev = latestOutputByOrder[orderId];
            const prevTime = new Date(prev.createdAt || 0).getTime();
            const curTime = new Date(f.createdAt || 0).getTime();
            if (curTime >= prevTime) latestOutputByOrder[orderId] = f;
          }
        });

      const newClassVisibility: { [fileTitle: string]: { [classId: string]: boolean } } = {};

      const layers = validLasFiles.map((file: any, index) => {
        const { lasType, newClasses, filename, lasFilename, ownerOrder } = file;
        let renderer: any = null;

        if (lasType === 'all_classes') {
          if (!ownerOrder) return null;
          const latestForOrder = latestOutputByOrder[ownerOrder];
          if (!latestForOrder || latestForOrder._id !== file._id) return null;
        }

        const colorUniqueValueInfos = getPointCloudPalette(lasType, newClasses).map((pointCloudClass) => ({
          values: [String(pointCloudClass.classId)],
          label: pointCloudClass.name,
          color: pointCloudClass.color,
          originalColor: pointCloudClass.color,
        }));

        if (lasType === 'all_classes') {
          renderer = {
            type: 'point-cloud-unique-value',
            field: 'CLASS_CODE',
            colorUniqueValueInfos,
            pointSizeAlgorithm: { type: 'fixed-size', size: 1 },
          };
        } else if (lasType === 'unclassified') {
          renderer = {
            type: 'point-cloud-rgb',
            field: 'RGB',
            pointSizeAlgorithm: { type: 'fixed-size', size: 1 },
          };
        }

        const title = getLasTitle(file);

        newClassVisibility[title] = buildAllVisibleClassMap(lasType, newClasses, classesPresentByFile[title] ?? null);

        const visibleInitial = lasType === 'unclassified'
          ? false
          : ownerOrder === selectedOrderId;

        const layer = new PointCloudLayer({
          url: `${file.esriUrl}`,
          title,
          ...(renderer && { renderer }),
          visible: visibleInitial,
          elevationInfo: {
            mode: 'absolute-height',
          },
        });
        (layer as any).ownerOrder = ownerOrder;
        (layer as any).lasType = lasType;

        return layer;
      });

      setClassVisibility((prev) => ({ ...prev, ...newClassVisibility }));
      const createdLayers = (layers.filter(Boolean) as PointCloudLayer[]) || [];
      setShowPointCloud(false);
      return createdLayers;
    } catch (err) {
      console.error('Error creating PointCloudLayers:', err);
      return [];
    }
  }, [projectData?.lasFiles, getLasTitle, selectedOrderId]);

  useEffect(() => {
    if (!params.id) {
      navigate(GetProjectDetailsRoute(params.id));
    } else {
      // The project and its data load at once; the layers that need the ArcGIS token wait for it
      // (tokenRegistered), so no credential dialog opens.
      loadProject();
    }
    return () => {
      dispatch(ResetProjectAction());
      ReactPannellum.destroy();
      dispatch(ClearPanoFeaturesAction());
      dispatch(ClearAnnotationSuccessAction());
      dispatch(ResetKmlAction());
    };
  }, []);
  // Define toggleTerrain at component level
  const toggleTerrain = async (maps: MapItem[], visible: boolean) => {
    const updateGroundVisibility = async (map: __esri.Map | null, mapName: string) => {
      if (!map) {
        console.warn(`${mapName} instance not available for toggleTerrain`);
        return;
      }

      try {
        await map.ground.when();
        map.ground.opacity = visible ? 1 : 0;

        if (mapName === 'CAD Map' && map.basemap) {
          map.basemap.baseLayers.forEach((layer) => {
            layer.visible = visible;
          });
        }
      } catch (err) {
        console.error(`${mapName} ground load error:`, err);
      }
    };
    for (const { map, name } of maps) {
      await updateGroundVisibility(map, name);
    }
  };
  (window as any).toggleTerrain = toggleTerrain;

  const getPointCloudLayer = useCallback((fileTitle: string): PointCloudLayer | null => {
    // Always check BOTH ref and map. After an order switch the ref may be
    // momentarily stale while the new layers are being set.
    const map = lasMapInstanceRef.current;
    const fromRef = [...pointCloudLayersRef.current]
      .reverse()
      .find((l) => l.title === fileTitle);
    if (fromRef) return fromRef;

    if (!map) return null;
    const allMapLayers = map.layers.toArray();
    const fromMap = [...allMapLayers]
      .reverse()
      .find((l) => l.type === 'point-cloud' && l.title === fileTitle) as PointCloudLayer | undefined;
    return fromMap ?? null;
  }, []);

  // Define toggleLasVisibility function
  const toggleLasVisibility = (filename: string, visible: boolean) => {
    setLasFilesDisplayStatuses((prev) => ({ ...prev, [filename]: visible }));
    const layer = getPointCloudLayer(filename);
    if (layer) {
      layer.visible = visible;
    }
  };

  const toggleUnknownClassVisibility = async (fileTitle: string, visible: boolean) => {
    // Update the visibility state for the "Unknown" class
    setClassVisibility((prev) => {
      const updated = {
        ...prev,
        [fileTitle]: {
          ...(prev[fileTitle] || {}),
          '-1': visible, // Use '-1' as the key for Unknown class
        },
      };
      const fileVisibility: { [classId: string]: boolean } = updated[fileTitle];

      // Get all visible known classes
      const knownVisible = Object.entries(fileVisibility)
        .filter(([id, v]) => id !== '-1' && v) // Exclude Unknown class ID
        .map(([id]) => parseInt(id, 10));

      Promise.resolve().then(async () => {
        const layer = getPointCloudLayer(fileTitle);
        if (!layer) return;
        if (!layer.loaded) await layer.load();

        // Get the classification list for this file
        const lasFile = projectData?.lasFiles?.find((f: any) => getLasTitle(f) === fileTitle);
        const knownClassIds = getNamedPointCloudClasses(lasFile?.lasType, lasFile?.newClasses)
          .map((cls) => cls.classId);

        // Get all unknown class IDs (0-255 excluding known ones); classes the user
        // explicitly hid stay hidden even while the Unknown bucket is visible
        const unknownClassIds = fullPointCloudPalette
          .map((entry) => entry.classId)
          .filter((id) => !knownClassIds.includes(id))
          .filter((id) => fileVisibility[String(id)] !== false);

        if (knownVisible.length === 0 && !visible) {
          // If no known classes are visible AND unknown is off, hide everything
          layer.visible = false;
          layer.filters = [];
          layer.definitionExpression = '1=0';
          setLasFilesDisplayStatuses((p) => ({ ...p, [fileTitle]: false }));
          return;
        }

        // Combine known visible classes with unknown classes (if visible)
        const combinedVisibleClasses = visible
          ? Array.from(new Set([...knownVisible, ...unknownClassIds]))
          : knownVisible;

        // If there are visible classes, show the layer
        if (combinedVisibleClasses.length > 0) {
          layer.visible = true;
          setLasFilesDisplayStatuses((p) => ({ ...p, [fileTitle]: true }));

          layer.filters = [{
            type: 'value',
            field: 'CLASS_CODE',
            mode: 'include',
            values: combinedVisibleClasses,
          }];
          layer.definitionExpression = `CLASS_CODE IN (${combinedVisibleClasses.join(',')})`;
        } else {
          layer.visible = false;
          layer.filters = [];
          layer.definitionExpression = '1=0';
          setLasFilesDisplayStatuses((p) => ({ ...p, [fileTitle]: false }));
        }
      });
      return updated;
    });
  };

  const toggleClassVisibility = async (fileTitle: string, classId: string, visible: boolean) => {
    setClassVisibility((prev) => {
      const updated = {
        ...prev,
        [fileTitle]: { ...(prev[fileTitle] || {}), [classId]: visible },
      };

      const fileVisibility = updated[fileTitle];
      const newVisibleClasses = Object.entries(fileVisibility)
        .filter(([id, v]) => id !== '-1' && v)
        .map(([id]) => parseInt(id, 10));

      // Check if Unknown class is visible
      const unknownVisible = fileVisibility?.['-1'] !== false;

      Promise.resolve().then(async () => {
        const layer = getPointCloudLayer(fileTitle);
        if (!layer) return;
        if (!layer.loaded) await layer.load();

        // Get known class IDs for this las file
        const lasFile = projectData?.lasFiles?.find((f: any) => getLasTitle(f) === fileTitle);
        const knownClassIds = getNamedPointCloudClasses(lasFile?.lasType, lasFile?.newClasses)
          .map((cls) => cls.classId);

        // Get all unknown class IDs (0-255 excluding known ones); classes the user
        // explicitly hid stay hidden even while the Unknown bucket is visible
        const unknownClassIds = fullPointCloudPalette
          .map((entry) => entry.classId)
          .filter((id) => !knownClassIds.includes(id))
          .filter((id) => fileVisibility[String(id)] !== false);

        if (newVisibleClasses.length === 0 && !unknownVisible) {
          layer.visible = false;
          layer.filters = [];
          layer.definitionExpression = '1=0';
          setLasFilesDisplayStatuses((p) => ({ ...p, [fileTitle]: false }));
          return;
        }

        layer.visible = true;
        setLasFilesDisplayStatuses((p) => ({ ...p, [fileTitle]: true }));

        // Combine visible known classes + all unknown classes
        const combinedVisibleClasses = unknownVisible
          ? Array.from(new Set([...newVisibleClasses, ...unknownClassIds]))
          : newVisibleClasses;

        layer.filters = [{ type: 'value', field: 'CLASS_CODE', mode: 'include', values: combinedVisibleClasses }];
        layer.definitionExpression = `CLASS_CODE IN (${combinedVisibleClasses.join(',')})`;
      });

      return updated;
    });
  };

  // Show or hide every class of a file at once with a single filter write per
  // layer — used by the header check-all and order switches instead of looping
  // toggleClassVisibility over each class.
  const setAllClassesVisibility = (fileTitle: string, visible: boolean) => {
    setClassVisibility((prev) => {
      const classMap: { [classId: string]: boolean } = {};
      Object.keys(prev[fileTitle] || {}).forEach((key) => {
        classMap[key] = visible;
      });
      classMap['-1'] = visible;
      return { ...prev, [fileTitle]: classMap };
    });

    Promise.resolve().then(async () => {
      const layer = getPointCloudLayer(fileTitle);
      if (!layer) return;
      if (!layer.loaded) await layer.load();
      const filterableLayer = layer as PointCloudLayer & { definitionExpression?: string };
      if (visible) {
        layer.visible = true;
        layer.filters = [];
        filterableLayer.definitionExpression = '';
      } else {
        layer.visible = false;
        layer.filters = [];
        filterableLayer.definitionExpression = '1=0';
      }
      setLasFilesDisplayStatuses((p) => ({ ...p, [fileTitle]: visible }));
    });
  };

  useEffect(() => {
    // Cleanup sliders when their panels close
    if (!classifiedPanelOpen && classSliderRef.current) {
      classSliderRef.current.destroy();
      classSliderRef.current = null;
    }
    if (!outputPanelOpen && outputSliderRef.current) {
      outputSliderRef.current.destroy();
      outputSliderRef.current = null;
    }
    if (!inputPanelOpen && inputSliderRef.current) {
      inputSliderRef.current.destroy();
      inputSliderRef.current = null;
    }

    // Only proceed if at least one panel is open
    if (!classifiedPanelOpen && !outputPanelOpen && !inputPanelOpen) {
      return;
    }

    const updatePointCloudSize = (layer: PointCloudLayer, size: number) => {
      if (!layer.renderer) return;
      const r = (layer as any).renderer.clone();
      if (!r.pointSizeAlgorithm) {
        r.pointSizeAlgorithm = { type: 'fixed-size', size };
      } else {
        r.pointSizeAlgorithm.size = size;
      }
      layer.renderer = r;
    };

    // Small delay to ensure DOM is ready
    const timeout = setTimeout(() => {
      // Initialize INPUT slider if panel is open
      if (inputPanelOpen && !inputSliderRef.current) {
        const inputContainer = document.getElementById('input-point-size-slider');
        if (inputContainer) {
          // Clear any existing content
          inputContainer.innerHTML = '';

          inputSliderRef.current = new Slider({
            container: inputContainer,
            min: 1,
            max: 10,
            values: [inputPointSize],
            steps: 1,
            visibleElements: { rangeLabels: true },
          });

          inputSliderRef.current.on(['thumb-change', 'thumb-drag'], (e) => {
            const size = e.value;
            setInputPointSize(size);
            pointCloudLayersRef.current.forEach((l) => {
              if ((l as any).renderer?.type !== 'point-cloud-rgb') return;
              if (!l.loaded) {
                l.when(() => updatePointCloudSize(l, size));
              } else {
                updatePointCloudSize(l, size);
              }
            });
          });
        }
      }

      // Initialize OUTPUT slider if panel is open
      if (outputPanelOpen && !outputSliderRef.current) {
        const outputContainer = document.getElementById('output-point-size-slider');
        if (!outputContainer) return;

        // Clear any existing content
        outputContainer.innerHTML = '';

        const outputLayer = point3DLayerRef.current;
        if (!outputLayer) return;

        const outputRendererRef = { current: null as __esri.UniqueValueRenderer | null };

        const applySize = (size: number) => {
          if (!outputLayer?.loaded || !outputRendererRef.current || !outputSliderRef.current) return;

          const renderer = outputRendererRef.current;
          renderer.uniqueValueInfos.forEach((info) => {
            const sym = info.symbol as __esri.SimpleMarkerSymbol;
            if (sym) sym.size = size;
          });
          if (renderer.defaultSymbol) {
            (renderer.defaultSymbol as __esri.SimpleMarkerSymbol).size = size;
          }
          outputLayer.renderer = renderer.clone();
          outputLayer.refresh?.();
        };

        outputSliderRef.current = new Slider({
          container: outputContainer,
          min: 1,
          max: 20,
          values: [outputPointSize],
          steps: 1,
          visibleElements: { rangeLabels: true },
        });

        const initRenderer = () => {
          if (!outputLayer?.loaded) return;

          const r = outputLayer.renderer as __esri.UniqueValueRenderer;
          if (!r) return;

          outputRendererRef.current = r.clone();

          const currentSize =
            (r.uniqueValueInfos[0]?.symbol as __esri.SimpleMarkerSymbol)?.size ??
            (r.defaultSymbol as __esri.SimpleMarkerSymbol)?.size ??
            10;

          if (outputSliderRef.current) {
            outputSliderRef.current.values = [currentSize];
          }
          setOutputPointSize(currentSize);
        };

        if (outputLayer.loaded) {
          initRenderer();
        } else {
          outputLayer.when(initRenderer);
        }

        outputSliderRef.current.on(['thumb-change', 'thumb-drag'], (e) => {
          const size = e.value as number;
          setOutputPointSize(size);
          applySize(size);
        });
      }

      // Initialize CLASSIFIED slider if panel is open
      if (classifiedPanelOpen && !classSliderRef.current) {
        const classifiedContainer = document.getElementById('classified-point-size-slider');
        if (classifiedContainer) {
          // Clear any existing content
          classifiedContainer.innerHTML = '';

          classSliderRef.current = new Slider({
            container: classifiedContainer,
            min: 1,
            max: 10,
            values: [classifiedPointSize],
            steps: 1,
            visibleElements: { rangeLabels: true },
          });

          classSliderRef.current.on(['thumb-change', 'thumb-drag'], (e) => {
            const size = e.value;
            setClassifiedPointSize(size);
            pointCloudLayersRef.current.forEach((l) => {
              if ((l as any).renderer?.type !== 'point-cloud-unique-value') return;
              if (!l.loaded) {
                l.when(() => updatePointCloudSize(l, size));
              } else {
                updatePointCloudSize(l, size);
              }
            });
          });
        }
      }
    }, 50);

    return () => clearTimeout(timeout);
  }, [classifiedPanelOpen, outputPanelOpen, inputPanelOpen, classifiedPointSize, outputPointSize, inputPointSize]);

  // Sync panoControlsEnabled with panoFeatures availability
  useEffect(() => {
    if (!panoFeatures || panoFeatures.length === 0) {
      setPanoControlsEnabled(false);
      setPanoVisible(false);
    } else {
      // Enable controls when panoFeatures are available
      setPanoControlsEnabled(true);
    }
  }, [panoFeatures]);

  // Function to create cone geometry based on yaw, FOV, and center point
  const createConeGeometry = (
    center: __esri.Point,
    yaw: number, // heading angle in degrees (0° = North, 90° = East)
    fov: number, // horizontal field of view in degrees
    radius = 15,
  ) => {
    const startAngle = yaw - fov / 2;
    const endAngle = yaw + fov / 2;
    const numSegments = 20;
    const points: number[][] = [];

    points.push([center.x, center.y]);

    for (let i = 0; i <= numSegments; i++) {
      const angle = startAngle + (i * (endAngle - startAngle)) / numSegments;
      const rad = (angle * Math.PI) / 180;

      // Adjust for map coordinates: 0° = North
      const x = center.x + radius * Math.sin(rad);
      const y = center.y + radius * Math.cos(rad);

      points.push([x, y]);
    }

    points.push([center.x, center.y]); // close polygon

    return {
      type: 'polygon',
      rings: [points],
      spatialReference: center.spatialReference,
    } as __esri.PolygonProperties;
  };
  // Function to update cone geometry based on yaw, FOV, and center point
  const updateConeGeometry = (yaw: number, hfov: number) => {
    if (!centerPointRef.current || !coneGraphicRef.current) return;

    const baseHeading = featureKappaRef.current;
    const totalYaw = baseHeading + yaw;
    setViewHeadingDeg(Math.round((((totalYaw % 360) + 360) % 360)));
    const newGeom = createConeGeometry(centerPointRef.current, totalYaw, hfov);
    coneGraphicRef.current.geometry = newGeom;
  };
  // Mouse Interaction handler functions (handlePanoMouseDown, handlePanoMouseUp) for pano viewer
  const handlePanoMouseDown = () => {
    const yaw = ReactPannellum.getYaw();
    const hfov = ReactPannellum.getHfov();
    // update cone wrt user interaction on the Pano viewer
    updateConeGeometry(yaw, hfov);
  };

  const handlePanoMouseUp = () => {
    const yaw = ReactPannellum.getYaw();
    const hfov = ReactPannellum.getHfov();
    // update cone wrt user interaction on the Pano viewer
    updateConeGeometry(yaw, hfov);
  };
  function bearing(from: __esri.Point, to: __esri.Point) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    let angle = (Math.atan2(dx, dy) * 180) / Math.PI;
    if (angle < 0) angle += 360;
    return angle;
  }
  async function proximityPoints(
    imageName: string,
    graphic: __esri.Graphic,
    geojsonLayer: __esri.FeatureLayer,
  ) {
    if (!projectOperator.isLoaded()) await projectOperator.load();

    const webMercatorSR = new SpatialReference({ wkid: 3857 });

    try {
      const results = await geojsonLayer.queryFeatures();
      const nearbyFeatures = results.features.filter((feature) => {
        if (feature.attributes.Image_Filename === imageName) return false;

        const geom1 = projectOperator.execute(graphic.geometry, webMercatorSR);
        const geom2 = projectOperator.execute(feature.geometry, webMercatorSR);

        const dist = distanceOperator.execute(geom1, geom2, 'meters');
        return dist < 5;
      });
      return nearbyFeatures;
    } catch (err) {
      console.error('Error querying features:', err);
      return [];
    }
  }

  async function updateNavArrows(
    currentFeature: __esri.Graphic,
    panoLayer: __esri.FeatureLayer,
  ) {
    // Select the nearest pano per 45-degree sector within MAX_NEIGHBOR_DISTANCE_METERS,
    // client-side off the already-loaded pano layer (the server /pano/neighbors endpoint
    // is no longer called).
    const kappa = featureKappaRef.current;
    const neighbors: PanoNeighbor[] = [];
    try {
      if (!projectOperator.isLoaded()) await projectOperator.load();
      const sr = new SpatialReference({ wkid: 3857 });
      const fromPoint = currentFeature.geometry
        ? (projectOperator.execute(currentFeature.geometry, sr) as __esri.Point)
        : null;
      const currentFilename = currentFeature.attributes.filename;
      if (fromPoint) {
        // distanceOperator is PLANAR: on Web Mercator it inflates distances by
        // 1/cos(latitude). Scale back to true ground metres so the radius cutoff is
        // real. Ranking is unaffected — the scale factor is locally constant.
        const trueMeterScale = Math.cos(
          Math.atan(Math.sinh(fromPoint.y / WEB_MERCATOR_RADIUS_METERS)),
        );
        const { features } = await panoLayer.queryFeatures();
        // `Map` is the Esri Map class in this file (see import above), so the JS Map
        // must be reached through globalThis.
        const bestBySector = new globalThis.Map<number, { neighbor: PanoNeighbor; dist: number }>();
        features.forEach((f) => {
          const atts = f.attributes;
          if (!f.geometry || !atts?.filename || !atts?.filepath) return;
          if (atts.filename === currentFilename) return;
          const toPoint = projectOperator.execute(f.geometry, sr) as __esri.Point;
          const dist = distanceOperator.execute(fromPoint, toPoint, { unit: 'meters' }) * trueMeterScale;
          if (!(dist > 0) || dist > MAX_NEIGHBOR_DISTANCE_METERS) return;
          const bearingWorld = bearing(fromPoint, toPoint);
          const rel360 = (((bearingWorld - kappa) % 360) + 360) % 360;
          const sector = Math.round(rel360 / NAV_ARROW_LOCK_DEGREES) % 8;
          const relBearing = rel360 > 180 ? rel360 - 360 : rel360;
          const existing = bestBySector.get(sector);
          if (!existing || dist < existing.dist) {
            bestBySector.set(sector, { neighbor: { feature: f, sector, relBearing, dist }, dist });
          }
        });
        neighbors.push(...[...bestBySector.values()].map((v) => v.neighbor));
      }
    } catch (err) {
      console.error('Error selecting pano neighbors:', err);
    }
    // A newer pano loaded while we were querying (currentFeatureRef moved on) → its own updateNavArrows now
    // owns the arrows + keyboard neighbors. Discard this stale run rather than overwriting them with the
    // previous pano's result.
    if (currentFeatureRef.current !== currentFeature) return;
    panoNeighborsRef.current = neighbors;

    // Re-sync the cone to the current view yaw. The cone stays kappa-anchored (per
    // AUT-6103); this is a no-op if the cone graphic isn't set up yet.
    updateConeGeometry(ReactPannellum.getYaw?.() ?? 0, ReactPannellum.getHfov?.() ?? 90);

    // Draw only the nearest few neighbours; the rest stay in panoNeighborsRef for the
    // keyboard.
    const shown = [...neighbors].sort((a, b) => a.dist - b.dist).slice(0, MAX_VISIBLE_ARROWS);

    // Fetch signed URLs in parallel, then register each neighbour scene lazily
    // (autoLoad: false). The image is downloaded only when its arrow is actually
    // clicked, so a single navigation doesn't eagerly pull several full panoramas.
    const signedUrls = await Promise.all(
      shown.map((n) => dispatch(getSignedURLThunk(n.feature.attributes.filepath))),
    );

    const arrows: NavArrow[] = [];
    shown.forEach((n, i) => {
      const signedUrl = signedUrls[i];
      if (!signedUrl) {
        console.warn(`Skipping arrow for sector ${n.sector}: failed to fetch signed URL`);
        return;
      }

      const sceneId = `scene_${n.feature.attributes.filename}`;
      ReactPannellum.addScene(sceneId, {
        type: 'equirectangular',
        imageSource: signedUrl,
        autoLoad: false,
      });
      arrows.push({ ...n, sceneId });
    });

    // Same guard after the signed-URL fetch: a newer pano must not have its arrows clobbered by this run.
    if (currentFeatureRef.current !== currentFeature) return;
    setNavArrows(arrows);
  }

  // Reusable function to load a pano, its cone, and the nav arrows
  // Resolve the pano a go_to_pano command targets: a named pano (filename) or the nearest pano to the
  // pole's WGS84 location, plus the world heading to FACE the pole (bearing pano→pole, unless the command
  // gave an explicit heading). Returns null if the pano layer has no usable candidate.
  async function resolvePanoTarget(
    panoLayer: __esri.FeatureLayer,
    cmd: { lng: number; lat: number; filename?: string; heading_deg?: number },
  ): Promise<{ feature: __esri.Graphic; worldHeading?: number } | null> {
    try {
      if (!projectOperator.isLoaded()) await projectOperator.load();
      const sr = new SpatialReference({ wkid: 3857 });
      const polePoint = projectOperator.execute(
        new Point({ x: cmd.lng, y: cmd.lat, spatialReference: new SpatialReference({ wkid: 4326 }) }),
        sr,
      ) as __esri.Point;
      const { features } = await panoLayer.queryFeatures();
      const candidates = features.filter((f) => f.geometry && f.attributes?.filename && f.attributes?.filepath);
      let chosen: __esri.Graphic | null = cmd.filename
        ? (candidates.find((f) => f.attributes.filename === cmd.filename) ?? null)
        : null;
      if (!chosen) {
        // Nearest pano to the pole. Planar 3857 distance is fine for RANKING (scale is locally constant).
        let best = Infinity;
        candidates.forEach((f) => {
          const p = projectOperator.execute(f.geometry as __esri.Point, sr) as __esri.Point;
          const d = distanceOperator.execute(polePoint, p, { unit: 'meters' });
          if (d < best) { best = d; chosen = f; }
        });
      }
      if (!chosen) return null;
      const chosenPoint = projectOperator.execute(chosen.geometry as __esri.Point, sr) as __esri.Point;
      const worldHeading = cmd.heading_deg ?? bearing(chosenPoint, polePoint);
      return { feature: chosen, worldHeading };
    } catch (err) {
      console.error('Error resolving pano target:', err);
      return null;
    }
  }

  async function loadPanoScene(
    feature: __esri.Graphic,
    panoLayer: __esri.FeatureLayer,
    targetSceneId: string = 'firstScene',
    preserveHeading: boolean = false,
    targetWorldHeading?: number,
  ) {
    try {
      if (!lasViewRef.current) return;
      setPanoError(null); // clear the pano error everytime scene loads
      setPanoWarning(null); // clear the pano warning everytime scene loads
      // Capture where the user is currently looking, in world terms, BEFORE the refs are
      // overwritten with the incoming pano (AUT-6181).
      const previousKappa = featureKappaRef.current;
      const previousYaw = ReactPannellum.getYaw?.() ?? 0;
      const { filepath, filename, kappa, remarks } = feature.attributes;

      if (!filepath) {
        throw new Error('Missing required attribute: filepath');
      }

      if (!filename) {
        throw new Error('Missing required attribute: filename');
      }

      if (kappa === undefined || kappa === null) {
        setPanoWarning('Panorama direction may be inaccurate');
      }
      const finalKappa = kappa ?? 0; // uses 0 only if kappa is null or undefined

      if (remarks) {
        setPanoWarning('If the Pano image is not visible');
      }

      // Clean old cone
      if (coneGraphicRef.current) {
        lasViewRef.current.graphics.remove(coneGraphicRef.current);
        coneGraphicRef.current = null;
      }

      // Project to 3857
      const webMercatorSR = new SpatialReference({ wkid: 3857 });
      if (!projectOperator.isLoaded()) await projectOperator.load();

      const projectedCenter = projectOperator.execute(
        feature.geometry as __esri.Point,
        webMercatorSR,
      ) as __esri.Point;

      // Recenter on the new pano by translating the camera: shift its x/y by
      // the view-center-to-pano delta, which preserves zoom, tilt and heading
      // by construction. We avoid view.scale because a SceneView re-derives it
      // from the terrain under the view center, so tilt and pano-z-vs-ground
      // gaps skew it and any goTo that uses it compounds a zoom drift per hop.
      const view = lasViewRef.current;
      if (view.center) {
        const viewCenter = projectOperator.execute(view.center, webMercatorSR) as __esri.Point;
        const camera = view.camera.clone();
        const camPos = projectOperator.execute(camera.position, webMercatorSR) as __esri.Point;
        camPos.x += projectedCenter.x - viewCenter.x;
        camPos.y += projectedCenter.y - viewCenter.y;
        camera.position = camPos;
        view.goTo(camera).catch((e) => {
          if (e.name !== 'AbortError') {
            console.error('goTo error (pano recenter):', e);
          }
        });
      }

      centerPointRef.current = projectedCenter;
      featureKappaRef.current = finalKappa;

      // World heading (0=N, 90=E) the pano opens facing: what the user was looking at when
      // navigating (previousKappa + previousYaw), else the pano's own camera on a fresh open.
      // Pannellum yaw = heading − kappa, since world heading = kappa + yaw (matches the cone).
      // An explicit targetWorldHeading (e.g. Khora go_to_pano facing the pole) wins; otherwise keep the
      // user's current look (preserveHeading) or open at the pano's own camera.
      const worldHeading = targetWorldHeading !== undefined
        ? targetWorldHeading
        : (preserveHeading ? previousKappa + previousYaw : finalKappa);
      initialYawRef.current = ((((worldHeading - finalKappa) % 360) + 540) % 360) - 180; // → (-180, 180]
      setViewHeadingDeg(Math.round((((worldHeading % 360) + 360) % 360))); // seed the readout at the opening heading

      // Draw the cone at the same world heading the pano will open at.
      const hfov = ReactPannellum.getHfov() ?? 90;
      const coneGeom = createConeGeometry(
        projectedCenter,
        worldHeading,
        hfov,
      );

      const coneGraphic = new Graphic({
        geometry: coneGeom,
        symbol: {
          type: 'simple-fill',
          color: [255, 0, 0, 0.55],
          outline: null,
        },
      });

      lasViewRef.current.graphics.add(coneGraphic);
      coneGraphicRef.current = coneGraphic;
      currentFeatureRef.current = feature;

      // Get presigned URL
      const signedUrl = await dispatch(getSignedURLThunk(filepath));
      const objectId = feature?.attributes?.object_id;

      if (!signedUrl) {
        // keep a track on failed pano features
        if (objectId) {
          setFailedPanoIds((prev) =>
            (prev.includes(objectId) ? prev : [...prev, objectId]));
        }
        setPanoErrorClosed(false);
        setPanoWarning(null);
        setImageSource(signedUrl);
        setPanoError('Error loading Pano Image');
        console.warn(`Skipping scene ${filename} because signed URL is missing`);
        return; // Early return if no signed URL
      }

      // Successfully got signed URL - remove from failed list if it was there
      if (objectId) {
        setFailedPanoIds((prev) => prev.filter((id) => id !== objectId));
      }

      // set image source
      setImageSource(signedUrl);
      currentSceneIdRef.current = targetSceneId === 'firstScene' ? 'firstScene' : `scene_${filename}`;
    } catch (error) {
      console.error('Error in loadPanoScene:', error);
    }
  }

  // Populate the nav arrows once the pano has loaded
  useEffect(() => {
    if (!panoVisible) {
      setNavArrows([]);
      return undefined;
    }

    // Drop the previous pano's arrows immediately; updateNavArrows repopulates them
    // once the new scene is loaded.
    setNavArrows([]);

    let interval: any;

    interval = setInterval(async () => {
      if (ReactPannellum.isLoaded()) {
        clearInterval(interval);
        if (currentFeatureRef.current && panoLayerFeatureRef.current) {
          await updateNavArrows(
            currentFeatureRef.current,
            panoLayerFeatureRef.current,
          );
        }
      }
    }, 50);

    return () => clearInterval(interval);
  }, [imageSource, panoVisible]);

  // MEMORY LEAK FIX (fly-through / any pano navigation): the <ReactPannellum key={imageSource}> above remounts
  // the whole viewer on every scene change, but react-pannellum@0.2.16's componentWillUnmount only detaches
  // listeners — it never calls the outgoing Pannellum viewer's destroy(), so each one keeps its WebGL context
  // + equirect texture alive AND stays pinned in the library's module-level getViewers() array forever.
  // Walking N poles would otherwise leak N viewers (hit the browser's ~16 WebGL-context cap + climbing memory).
  // After each remount destroy every stale viewer and prune the array to just the live one; on pano close,
  // destroy them all. Pannellum.destroy() frees the renderer, so this reclaims the contexts + textures.
  useEffect(() => {
    const viewers: Array<{ destroy?: () => void }> = ReactPannellum.getViewers?.() ?? [];
    if (!panoVisible) {
      // Pano closed — the mounted viewer just unmounted un-destroyed; free every viewer + empty the array.
      viewers.forEach((viewer) => { try { viewer?.destroy?.(); } catch { /* already torn down */ } });
      viewers.length = 0;
      return;
    }
    // Pano switched — keep the LIVE viewer, destroy the rest. react-pannellum pushes the new viewer inside a
    // deferred setState callback, so by the time this passive effect runs the live one may be either the
    // current myPannellum (getViewer) or the last pushed — keep BOTH candidates so we never destroy the
    // displayed viewer (worst case one extra survives a scene, reclaimed next round). Prune the array in place.
    const current = ReactPannellum.getViewer?.();
    const last = viewers[viewers.length - 1];
    const survivors = viewers.filter((viewer) => {
      if (viewer === current || viewer === last) return true;
      try { viewer?.destroy?.(); } catch { /* already torn down */ }
      return false;
    });
    if (survivors.length !== viewers.length) { viewers.length = 0; viewers.push(...survivors); }
  }, [imageSource, panoVisible]);

  // Pano loading error
  useEffect(() => {
    if (!panoVisible || !panoRef.current) return;

    const panoDiv = panoRef.current;

    const observer = new MutationObserver(() => {
      const errorEl = panoDiv.querySelector(
        '.pnlm-error-msg, .pnlm-info-box',
      ) as HTMLElement | null;

      if (errorEl && errorEl.innerText.trim()) {
        setPanoErrorClosed(false);
        setPanoWarning(null);
        setPanoError('Error loading Pano Image');
      }
    });

    observer.observe(panoDiv, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [panoVisible]);

  // Drive the compass rose. Each arrow sits at its 45-degree sector (view-relative,
  // 0 = top) and points radially outward, so "ahead" is vertical and diagonals aim at the
  // corners. Rotation comes from the arrow's pixel position (atan2), not the seat angle,
  // so it stays radial on the squashed ellipse. No Pannellum view event, so poll yaw on rAF.
  useEffect(() => {
    if (!panoVisible || !navArrows.length) return undefined;

    let rafId = 0;
    let lastYaw: number | null = null;

    const tick = () => {
      if (ReactPannellum.isLoaded?.()) {
        const yaw = ReactPannellum.getYaw?.() ?? 0;
        if (yaw !== lastYaw) {
          const els = compassRef.current?.children;
          if (els) {
            navArrows.forEach((n, i) => {
              const el = els[i] as HTMLElement | undefined;
              if (!el) return;
              const seat = ((n.sector * NAV_ARROW_LOCK_DEGREES - yaw + 540) % 360) - 180;
              const rad = (seat * Math.PI) / 180;
              const x = COMPASS_RADIUS_PX * Math.sin(rad);
              const y = -COMPASS_RADIUS_PX * COMPASS_SQUASH_Y * Math.cos(rad);
              const rotation = (Math.atan2(x, -y) * 180) / Math.PI;
              el.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${rotation}deg)`;
            });
          }
          lastYaw = yaw;
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafId);
  }, [panoVisible, navArrows]);

  // pano keyboard navigation shortcut listener
  useEffect(() => {
    if (!panoVisible || !panoRef.current) return;

    const handleKeyDown = async (e: KeyboardEvent) => {
      // Ignore when typing in input fields
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      const offset = KEY_OFFSET[e.key];
      if (offset === undefined) return;
      e.preventDefault();

      // Resolve the key to the nearest neighbor by angle, relative to the current
      // view: wanted bearing (kappa-relative) = current view yaw + key offset.
      const yaw = ReactPannellum.getYaw?.() ?? 0;
      const wanted = yaw + offset;
      let best: PanoNeighbor | null = null;
      let bestDiff = Infinity;
      panoNeighborsRef.current.forEach((n) => {
        const diff = Math.abs(((n.relBearing - wanted + 540) % 360) - 180);
        if (diff < bestDiff) {
          bestDiff = diff;
          best = n;
        }
      });
      if (best && bestDiff <= KEYBOARD_MATCH_THRESHOLD) {
        await loadPanoScene(best.feature, panoLayerFeatureRef.current, 'firstScene', true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [panoVisible]);

  const getHas3dForSiteId = useCallback((siteId: string): boolean => {
    if (!orders || !Array.isArray(orders)) return false;

    for (const order of orders) {
      if (!order.cadFiles || !Array.isArray(order.cadFiles)) continue;

      const cadFile = order.cadFiles.find((cad: any) => cad._id === siteId);
      if (cadFile && typeof cadFile.has3d === 'boolean') {
        return cadFile.has3d;
      }
    }

    return false; // default to on-the-ground
  }, [orders]);

  const getElevationInfo = (siteId: string): ElevationInfo => {
    const has3d = getHas3dForSiteId(siteId);
    const info: ElevationInfo = {
      mode: has3d ? 'absolute-height' : 'on-the-ground',
    };
    return info;
  };

  const extractColorFromRenderer = (drawingInfo: any): string => {
    try {
      if (!drawingInfo || !drawingInfo.renderer) {
        return '#0078D4'; // Default ArcGIS blue
      }

      const { renderer } = drawingInfo;

      // Simple Renderer
      if (renderer.type === 'simple' && renderer.symbol) {
        const { symbol } = renderer;

        // For point symbols
        if (symbol.color && Array.isArray(symbol.color)) {
          const [r, g, b, a = 255] = symbol.color;
          return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
        }

        // For line symbols
        if (symbol.outline && symbol.outline.color && Array.isArray(symbol.outline.color)) {
          const [r, g, b, a = 255] = symbol.outline.color;
          return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
        }
      }

      // Unique Value Renderer - use first unique value's color
      if (renderer.type === 'uniqueValue' && renderer.uniqueValueInfos && renderer.uniqueValueInfos.length > 0) {
        const firstInfo = renderer.uniqueValueInfos[0];
        if (firstInfo.symbol && firstInfo.symbol.color && Array.isArray(firstInfo.symbol.color)) {
          const [r, g, b, a = 255] = firstInfo.symbol.color;
          return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
        }
      }

      // Class Breaks Renderer - use first class break's color
      if (renderer.type === 'classBreaks' && renderer.classBreakInfos && renderer.classBreakInfos.length > 0) {
        const firstInfo = renderer.classBreakInfos[0];
        if (firstInfo.symbol && firstInfo.symbol.color && Array.isArray(firstInfo.symbol.color)) {
          const [r, g, b, a = 255] = firstInfo.symbol.color;
          return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
        }
      }

      return '#0078D4'; // Default color if no valid color found
    } catch (err) {
      console.warn('Error extracting color from renderer:', err);
      return '#0078D4';
    }
  };

  // Keyed on the target's coordinates, so the flight repeats only when the point itself moves.
  useEffect(() => {
    orderref.current = selectedOrderId;
    if (!selectedOrderId || !lasViewRef.current || !selectedFlightTarget) return;

    const view = lasViewRef.current;
    const { longitude, latitude } = selectedFlightTarget;
    reactiveUtils.whenOnce(() => view.ready).then(() => {
      if (!view) return;
      view.goTo(
        {
          target: { longitude, latitude },
          distance: 5000,
          heading: 0,
          tilt: 0,
        },
        {
          duration: 1500,
        },
      ).catch((e) => {
        if (e.name !== 'AbortError') {
          console.error(e);
        }
      });
    });
  }, [selectedOrderId, selectedFlightTarget?.longitude, selectedFlightTarget?.latitude]);
  useEffect(() => {
    let active = true;
    let lasViewToCleanup: __esri.SceneView | null = null;
    const urlsToRevoke: string[] = [];

    // Wait for token registration to complete before creating layers
    if (!tokenRegistered || !lasRef.current) return;
    // Determine the initial order to load
    const getInitialOrder = () => {
      if (!sortedPlacedOrders.length) return null;
      return sortedPlacedOrders[0];
    };

    const initialOrder = getInitialOrder();
    const initialOrderId = initialOrder?._id || selectedOrderId;

    const orderUnits = initialOrder?.cadFiles?.find((cad: any) => cad.units)?.units || 'meter';
    const isUSFoot = orderUnits === 'US survey foot';

    // If no order is selected yet, set the initial order
    if (!selectedOrderId && initialOrderId) {
      dispatch(SetOrderSelectedAction(initialOrderId));
    }
    const cadIds = initialOrder?.cadFiles?.map((cad: any) => cad._id) || [];
    setorderid(selectedOrderId);
    orderref.current = selectedOrderId;

    const initializeLayers = async () => {
      // get the combined extent

      const lasMap = new Map({
        basemap: 'streets',
        // Shift the terrain by -geoidOffsetZ so NAVD88-orthometric data renders flush
        // at native Z, instead of offsetting every data layer (see OffsetElevationLayer).
        ground: new Ground({ layers: [new OffsetElevationLayer({ offset: groundOffset })] }),
        layers: [],
      });
      lasMapInstanceRef.current = lasMap;

      const lasView = new SceneView({
        container: lasRef.current,
        center: viewportCenter.center,
        zoom: viewportCenter.zoom,
        map: lasMap,
      });

      if (!active) {
        lasView.destroy();
        return;
      }
      lasViewToCleanup = lasView;
      lasViewRef.current = lasView;
      (window as any).lasView = lasView;
      setlasView(lasView);
      lasView.ui.remove('zoom');
      lasView.ui.remove('navigation-toggle');
      lasView.ui.remove('compass');
      const zoomWidget_las = new Zoom({
        view: lasView,
      });
      const navigationToggle_las = new NavigationToggle({
        view: lasView,
      });
      const compass_las = new Compass({
        view: lasView,
      });
      lasView.ui.add(zoomWidget_las, 'top-right');
      lasView.ui.add(navigationToggle_las, 'top-right');
      lasView.ui.add(compass_las, 'top-right');
      const basemapWidget = new Basemap({
        view: lasView,

      });
      basemapRef.current = basemapWidget;
      lasView.ui.add(basemapWidget, 'top-right');

      // Default measurements to feet; US-survey-foot orders use the matching unit.
      const imperialLengthUnit = isUSFoot ? 'us-feet' : 'feet';
      const imperialAreaUnit = isUSFoot ? 'square-us-feet' : 'square-feet';
      const distanceMeasurement3d = new DirectLineMeasurement3D({
        view: lasView,
        unit: imperialLengthUnit,
      });
      const areaMeasurement3d = new AreaMeasurement3D({
        view: lasView,
        unit: imperialAreaUnit,
      });
      const lasdistance3d = new DistanceMeasurement({
        view: lasView,
        measurement: distanceMeasurement3d,
      });
      lasView.ui.add(lasdistance3d, 'top-right');
      setDistanceWidget3d(lasdistance3d);
      const lasArea3d = new AreaMeasurement({
        view: lasView,
        measurement: areaMeasurement3d,
      });
      lasView.ui.add(lasArea3d, 'top-right');
      setAreaWidget3d(lasArea3d);
      const measurementUnitToggle = new MeasurementUnitToggle({
        view: lasView,
        distanceMeasurement: distanceMeasurement3d,
        areaMeasurement: areaMeasurement3d,
        imperialLengthUnit,
        imperialAreaUnit,
      });
      lasView.ui.add(measurementUnitToggle, 'top-right');
      const lasTerrain = new TerrainWidget({
        view: lasView,
      });
      terrainRef.current = lasTerrain;
      lasTerrain.on('terrain-toggle', ({ active }) => {
        const terrainOff = !!active;
        toggleTerrain([{ map: lasMapInstanceRef.current, name: 'LAS Map' }], terrainOff);
      });
      lasView.on('click', async (event) => {
        islasViewRef.current = true;
        if (measuringRefDistance.current || measuringRefArea.current) return;
        // annotation click event in 3D Viewer
        const screenPoint = lasView.toScreen(event.mapPoint);
        const lasRect = lasView.container.getBoundingClientRect();
        const globalX = screenPoint.x;
        const globalY = screenPoint.y;
        const x = Math.min(Math.max(globalX, 0), lasRect.width);
        const y = Math.min(Math.max(globalY, 0), lasRect.height);
        setPopupXY({ x, y });
        const EXCLUDED = new Set([
          'objectid', 'OBJECTID', '__OBJECTID', 'gid', 'origin_epsg', 'srid', 'site_id',
          'local_layer_id', 'timestamp', 'block_def_id', 'shape', 'SHAPE',
          'Shape__Area', 'Shape__Length', 'has3d', 'layer_id', 'outer_p',
          'OUTER_P', 'Outer_P',
        ]);

        // Collect all sublayers from geodatabase groups for precise hit-testing
        const gdbSublayers = (geodatabaseLayersRef.current || []).flatMap((group) => {
          if (group.layers) {
            return (group.layers as any).toArray();
          }
          return [group];
        });

        // Collect all sublayers from shapefile groups for precise hit-testing
        const shpSublayers = (shapefileLayersRef.current || []).flatMap((group) => {
          if (group.layers) {
            return (group.layers as any).toArray();
          }
          return [group];
        });

        // Build the popup display object for a single feature graphic.
        const buildFeatureDisplay = async (graphic: __esri.Graphic, layer: FeatureLayer) => {
          const objectId = graphic.attributes?.OBJECTID ?? graphic.attributes?.objectid;
          const fieldAliasMap: Record<string, string> = {};
          (layer.fields || []).forEach((f) => {
            fieldAliasMap[f.name] = f.alias || f.name;
          });
          const geomType = (layer.geometryType || '').replace('esriGeometry', '').toLowerCase();

          let attrs = { ...graphic.attributes };
          let { geometry } = graphic;
          try {
            const result = await layer.queryFeatures({
              objectIds: [objectId],
              outFields: ['*'],
              returnGeometry: true,
              outSpatialReference: lasView.spatialReference,
            });
            if (result.features.length > 0) {
              attrs = { ...result.features[0].attributes };
              if (result.features[0].geometry) {
                geometry = result.features[0].geometry;
              }
            }
          } catch (err) {
            console.warn('queryFeatures failed, using hitTest attrs', err);
          }

          const layerIdStr = String(attrs.layer_id ?? attrs.LAYER_ID ?? '');
          const meta = featureListRef.current.find((f) => String(f.value) === layerIdStr);
          // Prioritize layer-level metadata (set for GDB) over feature-level layer_id lookup
          const layerName = (layer as any).layerName || meta?.label || layer.title || 'Feature';
          const layerColor = (layer as any).layerColor || meta?.color || '#cccccc';

          // ── Shape metric helpers (matching old showPopupInfo conversions) ──
          const Area_Shape = (value: number) => Number((value * 10000000000).toFixed(4));
          const Length_Shape = (value: number) => Number((value * 100000).toFixed(4));

          const display: Record<string, any> = {};

          if (attrs.origin_epsg != null && attrs.origin_epsg !== '') {
            display['Given EPSG'] = attrs.origin_epsg;
          }

          if (geomType === 'polygon') {
            const rawArea = attrs.Shape__Area ?? attrs['st_area(shape)'];
            const rawPeri = attrs.Shape__Length ?? attrs['st_perimeter(shape)'];
            if (rawArea != null) display['Shape Area'] = `${Area_Shape(rawArea)} sq m`;
            if (rawPeri != null) display['Shape Length'] = `${Length_Shape(rawPeri)} m`;
            const outerP =
              attrs.outer_p ??
              attrs.OUTER_P ??
              attrs.Outer_P;

            if (outerP != null && outerP !== '') { display['Outer P'] = outerP; }
          } else if (geomType === 'polyline') {
            const rawLen = attrs.Shape__Length ?? attrs['st_length(shape)'];
            if (rawLen != null) display['Shape Length'] = `${Length_Shape(rawLen)} m`;
          }

          const ALREADY_SHOWN = new Set([
            'origin_epsg', 'Outer P', 'OUTER_P',
            'st_area(shape)', 'st_perimeter(shape)', 'st_length(shape)',
          ]);
          Object.entries(attrs).forEach(([key, val]) => {
            if (EXCLUDED.has(key)) return;
            if (ALREADY_SHOWN.has(key)) return;
            if (val === undefined || val === null || val === '') return;
            const aliasKey = fieldAliasMap[key] || key;
            display[aliasKey] = val;
          });

          // Append Khora's per-feature analysis (add_feature_analysis) into THIS feature's popup, if Khora
          // computed one for this layer. Match the layer name AND the join key FUZZILY: displayed layer
          // titles vary, key fields get renamed (Pole_ID -> "Pole ID"), and numeric keys stringify
          // DIFFERENTLY in Python vs JS (Python str(8.0)="8.0" but JS String(8.0)="8"), so a naive exact
          // match silently misses for EVERY feature. No new layer; the analysis rides `khora_analysis`.
          const analyses = featureAnalysisRef.current;
          if (Object.keys(analyses).length) {
            const norm = (s: string) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
            const wantedLayer = norm(layer.title || layerName || '');
            const layerKey = Object.keys(analyses).find((name) => norm(name) === wantedLayer);
            const analysisForLayer = layerKey ? analyses[layerKey] : undefined;
            // Only inject the "Khora Analysis" section when the user pinned this analysis to the
            // dashboard — the dashboard pin is the on/off switch for the popup section.
            if (analysisForLayer?.keyField && analysisForLayer.onDashboard) {
              const target = norm(analysisForLayer.keyField);
              const keyAttr = Object.keys(attrs).find((k) => norm(k) === target);
              const keyVal = keyAttr != null ? attrs[keyAttr] : undefined;
              const byKey = analysisForLayer.byKey || {};
              // canonicalize numeric keys through Number() so "8.0" (Python) and 8 (JS) both map to "8"
              const canon = (value: unknown): string => {
                const num = Number(value);
                return Number.isFinite(num) ? String(num) : String(value);
              };
              const matchKey = keyVal == null ? undefined
                : byKey[String(keyVal)] !== undefined ? String(keyVal)
                  : Object.keys(byKey).find((k) => canon(k) === canon(keyVal));
              if (matchKey) display.khora_analysis = byKey[matchKey];
              // Only surface a real miss: Khora computed an analysis for this layer but the clicked
              // feature's key didn't map. Silent in normal use; diagnostic when the popup section is absent.
              else {
                // eslint-disable-next-line no-console
                console.warn('[khora popup] analysis present but no key match', { layer: layer.title || layerName, keyField: analysisForLayer.keyField, keyAttr, keyVal, byKeySample: Object.keys(byKey).slice(0, 3) });
              }
            }
          }

          // Internal keys for PopupWindow header
          display.layerName = layerName;
          display.layerColor = layerColor;
          display.geometry = geometry;

          // A Khora "Dashboard layer" may limit which attributes its popup shows (popup_fields); keep
          // only those plus the header/geometry/chart keys the popup needs. Other layers are unaffected.
          const khoraPopupFields = (layer as any).khoraPopupFields;
          if (Array.isArray(khoraPopupFields) && khoraPopupFields.length) {
            const keep = new Set([...khoraPopupFields, 'layerName', 'layerColor', 'geometry', 'khora_chart', 'khora_analysis']);
            Object.keys(display).forEach((k) => { if (!keep.has(k)) delete display[k]; });
          }

          return { display, geometry, geomType };
        };

        const isPopupLayer = (layer: any) => layer?.type === 'geojson'
          && layer.title !== 'Pano Features'
          && layer.title !== 'KML Features'
          && layer.title !== 'Text Labels';

        // Include the Khora "Dashboard layers" so a click on one of their features opens a popup too.
        const khoraLayers = Object.values(khoraLayersRef.current).map((entry) => entry.layer);

        // HitTest on Geodatabase, shapefile, Khora and Pano layers
        const hit = await lasView.hitTest(event, {
          include: [...gdbSublayers, ...shpSublayers, ...khoraLayers, panoLayerFeatureRef.current].filter(Boolean),
        });

        // Features hit directly on rendered pixels (outlines, points, lines).
        const hitTargets = hit.results
          .filter((r): r is __esri.GraphicHit => r.type === 'graphic' && isPopupLayer(r.layer))
          .map((r) => ({ graphic: r.graphic, layer: r.graphic.layer as FeatureLayer }));

        // Polygons render outline-only (no fill), so hitTest misses interior
        // clicks — query visible polygon layers for any feature containing the
        // clicked point so the whole polygon is selectable.
        const polygonLayers = [...gdbSublayers, ...shpSublayers]
          .filter((l: any) => isPopupLayer(l) && l.geometryType === 'polygon' && l.visible !== false);
        let queryTargets: { graphic: __esri.Graphic; layer: FeatureLayer }[] = [];
        if (event.mapPoint && polygonLayers.length > 0) {
          const perLayer = await Promise.all(
            polygonLayers.map(async (layer: any) => {
              try {
                const res = await layer.queryFeatures({
                  geometry: event.mapPoint,
                  spatialRelationship: 'intersects',
                  returnGeometry: true,
                  outFields: ['*'],
                  outSpatialReference: lasView.spatialReference,
                });
                return res.features.map((f: __esri.Graphic) => ({ graphic: f, layer }));
              } catch (err) {
                console.warn('polygon interior query failed', err);
                return [];
              }
            }),
          );
          queryTargets = perLayer.flat();
        }

        // Merge hit + interior-query targets, de-duplicating by layer + OBJECTID.
        const seen = new Set<string>();
        const targets = [...hitTargets, ...queryTargets].filter(({ graphic, layer }) => {
          const objectId = graphic.attributes?.OBJECTID ?? graphic.attributes?.objectid;
          const key = `${(layer as any).uid}-${objectId}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        if (targets.length > 0) {
          // Single source of truth for selection — drives the dashboard focus + cinematic orbit.
          setSelectedFeature(targets[0].graphic);
          // Also expose a serializable summary to the chatbot so Khora can act on "this feature".
          dispatch(setSelectedGdbFeature(summarizeGdbFeature(targets[0].graphic, targets[0].layer)));
          const features = await Promise.all(
            targets.map(({ graphic, layer }) => buildFeatureDisplay(graphic, layer)),
          );

          const validFeatures = features.filter(Boolean);

          if (validFeatures.length > 0) {
            const highlightFeature = (index: number) => {
              highlightLayer.removeAll();
              const { geometry, geomType } = validFeatures[index] || validFeatures[0];
              if (!geometry) return;
              let highlightSymbol: any;
              if (geomType === 'polygon') {
                highlightSymbol = { type: 'simple-fill', color: [0, 255, 255, 0.2], outline: { color: [0, 255, 255, 1], width: 2 } };
              } else if (geomType === 'polyline') {
                highlightSymbol = { type: 'simple-line', color: [0, 255, 255, 1], width: 3 };
              } else {
                highlightSymbol = { type: 'simple-marker', color: [0, 255, 255, 1], size: 12, outline: { color: [255, 255, 255], width: 1 } };
              }
              highlightLayer.add(new Graphic({ geometry, symbol: highlightSymbol }));
            };

            (window as any).__highlightFeature = highlightFeature;
            highlightFeature(0);
            setFilteredFeatures(validFeatures.map((f) => f.display));
            setCurrentIndex(0);
            setShowPopup(true);
            SetPopupVisibility(true);
          } else {
            setShowPopup(false);
            highlightLayer.removeAll();
          }
        } else {
          setShowPopup(false);
          highlightLayer.removeAll();
        }

        // Navigate when the click landed on a pano feature.
        const panoResult = hit.results.find(
          (r): r is __esri.GraphicHit => r.type === 'graphic'
            && !!panoLayerFeatureRef.current
            && r.graphic.layer === panoLayerFeatureRef.current,
        );
        if (!panoResult) return; // Exit if the click wasn't on panoLayer
        const panoFeature = panoResult.graphic;
        await loadPanoScene(panoFeature, panoLayerFeatureRef.current, 'firstScene');
      });

      lasMapInstanceRef.current = lasMap;

      //  layer URLs and query URL based on units
      const layersQueryUrl = isUSFoot ? config.layersUrlFt : config.layersUrl;
      const text3DLayerUrl = isUSFoot ? config.text3DftLayer : config.text3Dlayer;
      const point3DLayerUrl = isUSFoot ? config.point3dftLayer : config.point3dLayer;
      const polyline3DLayerUrl = isUSFoot ? config.polyline3dftlayer : config.polyline3dLayer;
      const polygon3DLayerUrl = isUSFoot ? config.polygon3dftLayer : config.polygon3dLayer;

      const polylineLayer = new FeatureLayer({
        url: polyline3DLayerUrl,
        title: '3D_Polyline',
      });

      const polygonLayer = new FeatureLayer({
        url: polygon3DLayerUrl,
        title: '3D_Polygon',
      });

      const text3Dlayer = new FeatureLayer({
        url: text3DLayerUrl,
        title: '3D_Text',
        renderer: {
          type: 'simple',
          symbol: {
            type: 'simple-marker',
            size: 0,
            color: [0, 0, 0, 0],
            outline: {
              width: 0,
            },
          },
        },
      });

      const point3DLayer = new FeatureLayer({
        url: point3DLayerUrl,
        title: '3D_Point',
      });
      point3DLayerRef.current = point3DLayer;

      const featureLayersArray = [polylineLayer, polygonLayer, point3DLayer, text3Dlayer];
      setFeatureLayers(featureLayersArray);

      const highlightLayer = new GraphicsLayer({
        id: 'highlight-layer',
      });
      lasMap.add(highlightLayer);

      const addWarning = (message: string) => {
        setWarnings((prev) => [...prev, message]);
      };

      (window as any).polylineLayer = polylineLayer;
      (window as any).polygonLayer = polygonLayer;
      (window as any).point3DLayer = point3DLayer;
      (window as any).text3Dlayer = text3Dlayer;

      // FeatureLayer for zooming
      const fl = new FeatureLayer({
        outFields: ['*'],
        url: config.fl,
      });
      const flText = new FeatureLayer({
        outFields: ['*'],
        url: `${config.layerServiceUrl}/0`,
      });
      const flPoint = new FeatureLayer({
        outFields: ['*'],
        url: `${config.layerServiceUrl}/1`,
      });
      const flLine = new FeatureLayer({
        outFields: ['*'],
        url: `${config.layerServiceUrl}/2`,
      });
      fl.definitionExpression = `site_id = '${config.site_id}'`;
      // Query layers table and apply rendering
      const queryLayersTable = (siteId: string, has3D: boolean): void => {
        const params = new Query({
          returnGeometry: false,
          returnDistinctValues: true,
          outFields: config.layersoutFields_Arr,
          where: `site_id = '${siteId}'`,
        });
        query
          .executeQueryJSON(layersQueryUrl, params)
          .then((response: any) => {
            const uniqueValues: UniqueValue[] = response.features.map((feature: any) => ({
              value: feature.attributes.id?.toString() || '',
              color: feature.attributes[config.layersoutFields.color] || 'rgba(0,0,0,1)',
              linetype: feature.attributes[config.layersoutFields.line_type] || 'CONTINUOUS',
              linewidth: feature.attributes[config.layersoutFields.line_width] ?? 1,
              label: feature.attributes[config.layersoutFields.name] || 'Unknown Layer',
              font_size: feature.attributes[config.textoutFields.font_size] || 6,
            }));

            // Save to state for custom layer list UI
            setFeatureList(uniqueValues);
            const initialChecked: { [key: string]: boolean } = {};
            uniqueValues.forEach((item) => {
              initialChecked[item.value] = true;
            });
            setCheckedItems(initialChecked);

            // Apply rendering for points, lines, and polygons
            if (has3D) {
              manageFeatureLayerLine(uniqueValues, polylineLayer);
              manageFeatureLayerPolygon(uniqueValues, polygonLayer);
              manageFeatureLayerPoint(uniqueValues, point3DLayer);
              manageFeatureLayerText(uniqueValues, text3Dlayer);
            }
          })
          .catch((err: Error) => console.error('Layers table query failed:', err));
      };
      (window as any).queryLayersTable = queryLayersTable;

      const checkfor3dfeature = async (siteId: string): Promise<boolean> => {
        const params = new Query({
          returnGeometry: false,
          outFields: config.has3dField,
          where: `site_id = '${siteId}'`,
          groupByFieldsForStatistics: config.has3dField,
          outStatistics: config.has3dStatistics,
        });

        const queryurl = [config.has3dCountUrl, config.polyline3dLayer, config.point3dLayer, config.text3Dlayer]
        for (const url of queryurl) {
          try {
            const response: any = await query.executeQueryJSON(url, params);

            const found = response.features.some((feature: any) => {
              const attrs = feature.attributes;
              return attrs.has3d === 1 && attrs.count > 0;
            });
            if (found) {
              return true;
            }
          } catch (err) {
            console.error('Layers table query failed:', err);
          }
        }
        return false;
      };
      (window as any).checkfor3dfeature = checkfor3dfeature;

      const manageFeatureLayerText = (
        uniqueValues: UniqueValue[],
        layer: FeatureLayer,
      ): void => {
        if (!uniqueValues.length) {
          return;
        }
        layer.renderer = {
          type: 'simple',
          symbol: {
            type: 'simple-marker',
            size: 0,
            color: [0, 0, 0, 0],
            outline: { width: 0 },
          },
        };
        // Create LabelClasses for each unique value
        const labelClasses: LabelClass[] = uniqueValues.map(
          (item) =>
            new LabelClass({
              where: `layer_id = '${item.value}'`, // filter for each unique value
              labelExpressionInfo: { expression: '$feature.text' }, // your text field
              symbol: {
                type: 'text',
                color: item.color,
                font: {
                  size: item.font_size,
                  family: 'Arial',
                  weight: 'bold',
                },
                haloColor: 'white',
                haloSize: 0,
              },
              labelPlacement: 'above-center',
            }),
        );
        layer.labelingInfo = labelClasses;
        layer.labelsVisible = true;
        if (!lasMap.layers.includes(layer)) {
          lasMap.add(layer);
        }
      };

      const manageFeatureLayerLine = (
        uniqueValues: UniqueValue[],
        layer: FeatureLayer,
      ): void => {
        if (!uniqueValues.length) {
          return;
        }

        const rendererInfos: __esri.UniqueValueRenderer['uniqueValueInfos'] = uniqueValues.map(
          (item) => ({
            value: item.value,
            symbol: {
              type: 'simple-line',
              style: config.dashPatterns[item.linetype?.toUpperCase()] || 'solid',
              color: item.color,
              width:
                item.linewidth === 0
                  ? 0.5
                  : [-1, -2, -3].includes(item.linewidth)
                    ? 1
                    : item.linewidth > 0
                      ? item.linewidth / 5
                      : 0.1,
            },
            label: item.label,
          }),
        );

        layer.renderer = {
          type: 'unique-value',
          field: 'layer_id',
          uniqueValueInfos: rendererInfos,
        };
        if (!lasMap.layers.includes(layer)) {
          lasMap.add(layer);
        }
      };

      const manageFeatureLayerPolygon = (
        uniqueValues: UniqueValue[],
        layer: FeatureLayer,
      ): void => {
        if (!uniqueValues.length) {
          return;
        }

        const rendererInfos: __esri.UniqueValueRenderer['uniqueValueInfos'] = uniqueValues.map(
          (item) => ({
            value: item.value,
            symbol: {
              type: 'simple-fill',
              color: [0, 0, 0, 0],
              style: 'none',
              outline: {
                type: 'simple-line',
                style: config.dashPatterns[item.linetype?.toUpperCase()] || 'solid',
                color: item.color,
                width: item.linewidth === 0 ? 0.5 : [-1, -2, -3].includes(item.linewidth) ? 1 : item.linewidth > 0 ? item.linewidth / 5 : 0.1,
              },
            },
            label: item.label,
          }),
        );

        layer.renderer = {
          type: 'unique-value',
          field: 'layer_id',
          uniqueValueInfos: rendererInfos,
        };

        lasMap.add(polygonLayer);
      };

      const manageFeatureLayerPoint = (
        uniqueValues: UniqueValue[],
        layer: FeatureLayer,
      ): void => {
        if (!uniqueValues.length) {
          return;
        }

        const rendererInfos: __esri.UniqueValueRenderer['uniqueValueInfos'] = uniqueValues.map(
          (item) => ({
            value: item.value,
            symbol: {
              type: 'simple-marker',
              style: 'circle',
              size: 5,
              color: item.color, // Transparent fill
              outline: {
                type: 'simple-line',
                style: config.dashPatterns[item.linetype?.toUpperCase()] || 'solid',
                color: item.color,
                width: item.linewidth === 0 ? 0.5 : [-1, -2, -3].includes(item.linewidth) ? 1 : item.linewidth > 0 ? item.linewidth / 5 : 0.1,
              },
            },
            label: item.label,
          }),
        );

        layer.renderer = {
          type: 'unique-value',
          field: 'layer_id',
          uniqueValueInfos: rendererInfos,
        };
        lasMap.add(layer);
      };

      const applyLayerViewFilter = (siteId: string) => {

        [polylineLayer, polygonLayer, point3DLayer, text3Dlayer].forEach((layer) => {
          layer.featureEffect = null; // clear any existing featureEffect
          layer.definitionExpression = `site_id = '${siteId}'`;
        });
      };

      // Zoom to site function
      const zoomToSite = async (siteId: string): Promise<void> => {
        if (!siteId || siteId.trim() === '') {
          console.warn('Invalid site_id provided');
          return;
        }

        const defExpression = `site_id = '${siteId}'`;

        // Apply definition expressions
        fl.definitionExpression = defExpression;
        flText.definitionExpression = defExpression;
        flPoint.definitionExpression = defExpression;
        flLine.definitionExpression = defExpression;

        applyLayerViewFilter(siteId);

        // In zoomToSite and setLayerDefinitions:
        const elevationInfo = getElevationInfo(siteId);
        text3Dlayer.elevationInfo = elevationInfo;
        point3DLayer.elevationInfo = elevationInfo;
        polygonLayer.elevationInfo = elevationInfo;
        polylineLayer.elevationInfo = elevationInfo;

        const has3D = await checkfor3dfeature(siteId);
        queryLayersTable(siteId, has3D);

        // Query all layers in parallel and union their extents
        try {
          const extentResults = await Promise.allSettled([
            fl.queryExtent(),
            flText.queryExtent(),
            flPoint.queryExtent(),
            flLine.queryExtent(),
          ]);

          // Collect all valid extents
          const validExtents = extentResults
            .filter(
              (result): result is PromiseFulfilledResult<__esri.FeatureLayerQueryExtentResult> =>
                result.status === 'fulfilled' && result.value.extent != null,
            )
            .map((result) => result.value.extent);

          if (validExtents.length > 0) {
            // Union all extents into one
            const combinedExtent = validExtents.reduce((acc, extent) => acc.union(extent));
          }
        } catch (error) {
          console.error('Zoom query failed:', error);
        }
      };

      (window as any).zoomToSite = zoomToSite;

      const setDefs = (siteId: string) => {
        const defExpression = `site_id = '${siteId}'`;
        fl.definitionExpression = defExpression;
        applyLayerViewFilter(siteId);

        const elev = getElevationInfo(siteId);
        text3Dlayer.elevationInfo = elev;
        point3DLayer.elevationInfo = elev;
        polygonLayer.elevationInfo = elev;
        polylineLayer.elevationInfo = elev;
      };

      const loadAndZoomSelectedOrder = async () => {
        // Use the currently selected order or find the first order with data
        const currentOrderId = selectedOrderId || placedOrders.find((order) => {
          const hasCadFiles = order.cadFiles && order.cadFiles.length > 0;
          const hasClassifiedPC = projectData?.lasFiles?.some(
            (las: any) => las.lasType !== 'unclassified' && las.ownerOrder === order._id,
          );
          return hasCadFiles || hasClassifiedPC;
        })?._id;

        if (!currentOrderId) return;

        const targetOrder = sortedPlacedOrders.find((o) => o._id === currentOrderId);
        if (!targetOrder) return;
        const OrderSExtent = localStorage.getItem('extent')
        const viewportstore = localStorage.getItem('viewport')
        const viewport = viewportstore ? JSON.parse(viewportstore) : null;
        const ParsedExtent = JSON.parse(OrderSExtent)
        const selectedOrder = ParsedExtent[targetOrder._id]
        const zoomToDistance = (zoom: number) => {
          return 591657550 / Math.pow(2, zoom); // standard WebMercator approximation
        };
        const fallbackPoint = Object.values(ParsedExtent)[0];
        // add a condition like if selected order has extent then zoom to extent else zoom to the point with a default distance
        lasView.when(() => {

          if (
            selectedOrder &&
            selectedOrder.latitude != null &&
            selectedOrder.longitude != null
          ) {
            lasView.goTo(
              {
                center: [selectedOrder.longitude, selectedOrder.latitude],
                zoom: 16 // you can tweak this
              },
              {
                duration: 1500
              }
            ).catch((e) => {
              if (e.name !== "AbortError") {
                console.error("goTo error (selectedOrder):", e);
              }
            });

            return; // stop here
          }

          // CASE 2: Fallback to stored viewport
          if (
            viewport &&
            viewport.center &&
            viewport.zoom !== undefined
          ) {
            lasView.goTo(
              {
                center: viewport.center,
                zoom: viewport.zoom
              },
              {
                duration: 1500
              }
            ).catch((e) => {
              if (e.name !== "AbortError") {
                console.error("goTo error (viewport):", e);
              }
            });

            return;
          }

          // CASE 3: no order/viewport location. Frame the loaded data layers if
          // they're ready; otherwise default to a US-wide view. loadDataLayers
          // frames the data once it loads regardless.
          const dataLayers = [
            ...geodatabaseLayersRef.current,
            ...shapefileLayersRef.current,
          ].filter(Boolean);
          if (dataLayers.length) {
            lasView.goTo(dataLayers).catch((e) => {
              if (e.name !== 'AbortError') {
                console.error('goTo error (data fallback):', e);
              }
            });
          } else {
            lasView.goTo({ center: US_DEFAULT_CENTER, zoom: US_DEFAULT_ZOOM });
          }
        });
      };

      loadAndZoomSelectedOrder();

      const setLayerDefinitions = (siteId: string) => {
        if (!siteId || siteId.trim() === '') {
          console.warn('Invalid site_id provided');
          return;
        }
        const defExpression = `site_id = '${siteId}'`;
        fl.definitionExpression = defExpression;
        applyLayerViewFilter(siteId);
        const elevationInfo = getElevationInfo(siteId);
        text3Dlayer.elevationInfo = elevationInfo;
        point3DLayer.elevationInfo = elevationInfo;
        polygonLayer.elevationInfo = elevationInfo;
        polylineLayer.elevationInfo = elevationInfo;
      };

      (window as any).setLayerDefinitions = setLayerDefinitions;

      // toggleTerrain globally
      (window as any).toggleTerrain = toggleTerrain;
      (window as any).geodatabaseLayers = geodatabaseLayersRef.current;
      (window as any).shapefileLayers = shapefileLayersRef.current;

      (window as any).getGeodatabaseLayerByFileId = (fileId: string) => geodatabaseLayersRef.current.find((l) => (l as any).fileId === fileId);

      (window as any).getShapefileLayerByFileId = (fileId: string) => shapefileLayersRef.current.find((l) => (l as any).fileId === fileId);

      reactiveUtils.whenOnce(() => lasView.ready).then(async () => {
        setViewReady(true); // view has painted — safe to drop the loading overlay
        toggleTerrain([{ map: lasMapInstanceRef.current, name: 'LAS Map' }], true);
        let initialExt = lasView.extent.clone();
        initailExtentref.current = initialExt;
        const latVal = Math.round(initialExt.center.latitude);
        const longVal = Math.round(Math.abs(initialExt.center.longitude));
        // here 40 and 100 represents initial center for sceneview in redux
        // This condition is mainly handle when the user refreshes the browser the center is set to [40,-100] to check and assign the extent when the browser is refreshed
        if (latVal == parseInt('40') || longVal == parseInt('100')) {
          // call the extent function

          const currentOrderId = selectedOrderId || placedOrders.find((order) => {
            const hasCadFiles = order.cadFiles && order.cadFiles.length > 0;
            const hasClassifiedPC = projectData?.lasFiles?.some(
              (las: any) => las.lasType !== 'unclassified' && las.ownerOrder === order._id,
            );
            return hasCadFiles || hasClassifiedPC;
          })?._id;

          if (!currentOrderId) return;
          const targetOrder = sortedPlacedOrders.find((o) => o._id === currentOrderId);
          if (!targetOrder) return;
          const OrderSExtent = localStorage.getItem('extent')
          const viewportstore = localStorage.getItem('viewport')
          const viewport = viewportstore ? JSON.parse(viewportstore) : null;
          const ParsedExtent = OrderSExtent ? JSON.parse(OrderSExtent) : {};
          const selectedOrder = ParsedExtent[targetOrder._id]
          const fallbackPoint = Object.values(ParsedExtent)[0];
          lasView.when(() => {
            if (
              selectedOrder &&
              selectedOrder.latitude != null &&
              selectedOrder.longitude != null
            ) {
              lasView.goTo(
                {
                  center: [selectedOrder.longitude, selectedOrder.latitude],
                  zoom: 16 // you can tweak this
                },
                {
                  duration: 1500
                }
              ).catch((e) => {
                if (e.name !== "AbortError") {
                  console.error("goTo error (selectedOrder):", e);
                }
              });

              return; // stop here
            }

            //  CASE 2: Fallback to stored viewport
            if (
              viewport &&
              viewport.center &&
              viewport.zoom !== undefined
            ) {
              lasView.goTo(
                {
                  center: viewport.center,
                  zoom: viewport.zoom
                },
                {
                  duration: 1500
                }
              ).catch((e) => {
                if (e.name !== "AbortError") {
                  console.error("goTo error (viewport):", e);
                }
              });

              return;
            }

            //  CASE 3: no order/viewport location. Frame the loaded data layers
            //  if ready; otherwise default to a US-wide view.
            const dataLayers = [
              ...geodatabaseLayersRef.current,
              ...shapefileLayersRef.current,
            ].filter(Boolean);
            if (dataLayers.length) {
              lasView.goTo(dataLayers).catch((e) => {
                if (e.name !== 'AbortError') {
                  console.error('goTo error (data fallback):', e);
                }
              });
            } else {
              lasView.goTo({ center: US_DEFAULT_CENTER, zoom: US_DEFAULT_ZOOM });
            }
          });
        }
        const homelas = new Home({
          view: lasView,
        });
        const vp = new Viewpoint({
          targetGeometry: initialExt,
        });
        homelas.viewpoint = vp;
        setHomeWidget(homelas);

        try {
          // Get all point cloud layers in the map
          const pointCloudLayers = lasView.map.layers.filter(
            (layer) => layer.type === 'point-cloud',
          );

          if (pointCloudLayers.length === 0) {
            console.warn('No point cloud layers found in the map.');
          }
        } catch (err) {
          console.error('Error setting extent to point cloud layers:', err);
        }
      });

      const Area_Shape = (value) => Number((value * 10000000000).toFixed(4)); // approximating sq degree to sq metre
      const Length_Shape = (value) => Number((value * 100000).toFixed(4)); // approximating degree to metre

      // Monitor for WebGL context loss in the SceneView.
      // If a fatal WebGL error occurs (e.g., GPU reset, driver issue, tab suspended),
      // attempt to recover the view and restore the previous state.
      reactiveUtils.when(
        () => lasView.fatalError,
        () => {
          console.error(
            'Fatal Error! View has lost its WebGL context. Attempting to recover...',
          );

          // Preserve the current extent so the user’s view is not lost after recovery
          const currentExtent = lasView.extent;

          // Trigger ArcGIS WebGL recovery mechanism
          lasView.tryFatalErrorRecovery();

          // Reload the panorama scene to reinitialize WebGL-dependent resources
          loadPanoScene(
            currentFeatureRef.current,
            panoLayerFeatureRef.current,
            'first',
            true, // keep the user's current view after WebGL recovery
          );

          // Restore the extent once the view is recovered
          if (currentExtent) {
            lasView.extent = currentExtent;
          }
        },
      );
    };
    initializeLayers();

    return () => {
      active = false;
      if (lasViewToCleanup) {
        lasViewToCleanup.destroy();
      }
      setlasView(null);
      urlsToRevoke.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [projectId, tokenRegistered, orderIdsKey, sceneGeneration]);

  useEffect(() => {
    if (!tokenRegistered) return;
    if (sortedPlacedOrders.length > 0) {
      const isValidSelection = sortedPlacedOrders.some(
        (o) => o._id === selectedOrderId,
      );
      if (!isValidSelection) {
        dispatch(SetOrderSelectedAction(sortedPlacedOrders[0]._id));
      }
    }
  }, [sortedPlacedOrders, tokenRegistered]);

  // useEffect to handle loading Geodatabase and Shapefile layers based on selected order
  useEffect(() => {
    if (!tokenRegistered || !lasMapInstanceRef.current || !selectedOrderId) {
      return;
    }

    let active = true;

    const loadDataLayers = async () => {
      // 1. Cleanup: remove existing GDB/SHP layers from map
      if (geodatabaseLayersRef.current && geodatabaseLayersRef.current.length > 0) {
        lasMapInstanceRef.current.removeMany(geodatabaseLayersRef.current);
      }
      if (shapefileLayersRef.current && shapefileLayersRef.current.length > 0) {
        lasMapInstanceRef.current.removeMany(shapefileLayersRef.current);
      }

      const createGeodatabaseLayers = async (): Promise<any[]> => {
        try {
          if (!selectedOrder?.geoDbFiles || !Array.isArray(selectedOrder.geoDbFiles)) {
            console.warn('No geoDbFiles found in selected order.');
            return [];
          }

          // Sort by createdAt to find most recent with valid esriUrl
          const sortedValidFiles = selectedOrder.geoDbFiles
            .filter((file: any) => file.esriUrl && file.esriError?.toLowerCase() === 'success')
            .sort((a, b) => {
              const dateA = new Date(a.createdAt || 0).getTime();
              const dateB = new Date(b.createdAt || 0).getTime();
              return dateB - dateA; // Most recent first
            });

          if (!sortedValidFiles.length) {
            console.warn('No valid Geodatabase files found.');
            return [];
          }

          // Only load the most recent file
          const mostRecentFile = sortedValidFiles[0];
          const { esriUrl, filename, _id, createdAt } = mostRecentFile;
          // Record which gdb is loaded so the dashboard can scope org-shared pins to THIS gdb.
          dispatch(setLoadedGdbId(String(_id)));
          const fileName = filename?.substring(filename.lastIndexOf('/') + 1) || 'Geodatabase-0';

          if (Array.isArray(esriUrl)) {
            const sublayers: GeoJSONLayer[] = [];

            for (let i = 0; i < esriUrl.length; i++) {
              const [layerName, geojsonUrl] = Object.entries(esriUrl[i])[0];

              try {
                let renderer: any;

                // Lookup layer-specific styles directly passed from configuration
                const layerConfig = styleConfig.layers[layerName];

                // Holds color specifically intended for mapping React UI sidebar list items
                let layerColorString: string | undefined;

                // Apply mapped config instantly if defined in imported styleConfig
                if (layerConfig) {
                  renderer = getRendererForConfig(layerConfig, layerConfig.geometryType, (c) => { layerColorString = c; });
                }
                const layerProps: any = {
                  url: GetEsriGeoJsonUrl(geojsonUrl),
                  title: `${layerName}`,
                  outFields: ['*'],
                  visible: true,
                };

                // Add active configuration renderer to GeoJSONLayer class parameters
                if (renderer) {
                  layerProps.renderer = renderer;
                }

                const layer = new GeoJSONLayer(layerProps);

                // Once Esri has loaded the layer (so geometryType/fields are known),
                // apply the data-driven renderer rules (poles → 3D cylinder/cone, etc.).
                // Rules win over styleConfig (applied at construction) and over the
                // geometry-type default. This mirrors the Sidebar loader so the group
                // renders identically no matter which path adds it to the map — without
                // it, when this effect's group wins the race the Sidebar hydrates from
                // it and the pole rules are never applied.
                layer.when(() => {
                  const rule = findRule({
                    name: layerName,
                    geometryType: layer.geometryType,
                    fields: layer.fields ?? [],
                  });
                  if (rule) {
                    rule.apply(layer);
                    (layer as any).layerColor = rule.legendColor;
                    return;
                  }

                  // No rule matched — fall back to the geometry-type default when
                  // styleConfig didn't already provide a named renderer.
                  if (!layerConfig) {
                    let defConfig;
                    if (layer.geometryType === 'point' || layer.geometryType === 'multipoint') defConfig = styleConfig.defaults.point;
                    else if (layer.geometryType === 'polyline') defConfig = styleConfig.defaults.polyline;
                    else if (layer.geometryType === 'polygon') defConfig = styleConfig.defaults.polygon;

                    if (defConfig) {
                      const defRenderer = getRendererForConfig(defConfig, layer.geometryType, (c) => { layerColorString = c; });
                      if (defRenderer) {
                        layer.renderer = defRenderer;
                      }
                      (layer as any).layerColor = defConfig.symbol?.color;
                    }
                  }
                }).catch(console.error);

                (layer as any).fileId = _id;
                (layer as any).ownerOrder = selectedOrderId;
                (layer as any).layerId = i;
                (layer as any).layerName = layerName;
                if (layerColorString) {
                  (layer as any).layerColor = layerColorString;
                }

                sublayers.push(layer);
              } catch (layerErr) {
                console.error(`Error creating Geodatabase layer "${layerName}":`, layerErr);
              }
            }

            const groupLayer = new GroupLayer({
              id: _id,
              title: fileName,
              layers: sublayers,
              visible: true,
            });

            (groupLayer as any).ownerOrder = selectedOrderId;
            (groupLayer as any).fileId = _id;

            return [groupLayer];
          }
          return [];
        } catch (err) {
          console.error('Error creating Geodatabase layers:', err);
          return [];
        }
      };

      const createShapefileLayers = async (): Promise<any[]> => {
        try {
          if (!selectedOrder?.shpFiles || !Array.isArray(selectedOrder.shpFiles)) {
            console.warn('No shpFiles found in selected order.');
            return [];
          }

          // Sort by createdAt to find most recent with valid esriUrl
          const sortedValidFiles = selectedOrder.shpFiles
            .filter((file: any) => file.esriUrl && file.esriError?.toLowerCase() === 'success')
            .sort((a, b) => {
              const dateA = new Date(a.createdAt || 0).getTime();
              const dateB = new Date(b.createdAt || 0).getTime();
              return dateB - dateA; // Most recent first
            });

          if (!sortedValidFiles.length) {
            console.warn('No valid Shapefile files with esriUrl found.');
            return [];
          }

          // ONLY load the most recent file
          const mostRecentFile = sortedValidFiles[0];
          const { esriUrl, filename, _id, createdAt } = mostRecentFile;
          const fileName = filename?.substring(filename.lastIndexOf('/') + 1) || 'Shapefile-0';

          if (Array.isArray(esriUrl)) {
            const sublayers: GeoJSONLayer[] = [];

            for (let i = 0; i < esriUrl.length; i++) {
              const [layerName, geojsonUrl] = Object.entries(esriUrl[i])[0];

              try {
                let renderer: any;

                // Lookup layer-specific styles directly passed from configuration
                const layerConfig = styleConfig.layers[layerName];

                // Holds color specifically intended for mapping React UI sidebar list items
                let layerColorString: string | undefined;

                // Apply mapped config instantly if defined in imported styleConfig
                if (layerConfig) {
                  renderer = getRendererForConfig(layerConfig, layerConfig.geometryType, (c) => { layerColorString = c; });
                }
                const layerProps: any = {
                  url: GetEsriGeoJsonUrl(geojsonUrl),
                  title: `${layerName}`,
                  outFields: ['*'],
                  visible: true,
                };

                // Add active configuration renderer to GeoJSONLayer class parameters
                if (renderer) {
                  layerProps.renderer = renderer;
                }

                const layer = new GeoJSONLayer(layerProps);

                // Once Esri has loaded the layer (so geometryType/fields are known),
                // apply the data-driven renderer rules (poles → 3D cylinder/cone, etc.).
                // Rules win over styleConfig (applied at construction) and over the
                // geometry-type default. This mirrors the Sidebar loader so the group
                // renders identically no matter which path adds it to the map — without
                // it, when this effect's group wins the race the Sidebar hydrates from
                // it and the pole rules are never applied.
                layer.when(() => {
                  const rule = findRule({
                    name: layerName,
                    geometryType: layer.geometryType,
                    fields: layer.fields ?? [],
                  });
                  if (rule) {
                    rule.apply(layer);
                    (layer as any).layerColor = rule.legendColor;
                    return;
                  }

                  // No rule matched — fall back to the geometry-type default when
                  // styleConfig didn't already provide a named renderer.
                  if (!layerConfig) {
                    let defConfig;
                    if (layer.geometryType === 'point' || layer.geometryType === 'multipoint') defConfig = styleConfig.defaults.point;
                    else if (layer.geometryType === 'polyline') defConfig = styleConfig.defaults.polyline;
                    else if (layer.geometryType === 'polygon') defConfig = styleConfig.defaults.polygon;

                    if (defConfig) {
                      const defRenderer = getRendererForConfig(defConfig, layer.geometryType, (c) => { layerColorString = c; });
                      if (defRenderer) {
                        layer.renderer = defRenderer;
                      }
                      (layer as any).layerColor = defConfig.symbol?.color;
                    }
                  }
                }).catch(console.error);

                (layer as any).fileId = _id;
                (layer as any).ownerOrder = selectedOrderId;
                (layer as any).layerId = i;
                (layer as any).layerName = layerName;
                if (layerColorString) {
                  (layer as any).layerColor = layerColorString;
                }

                sublayers.push(layer);
              } catch (layerErr) {
                console.error(`Error creating Geodatabase layer "${layerName}":`, layerErr);
              }
            }

            const groupLayer = new GroupLayer({
              id: _id,
              title: fileName,
              layers: sublayers,
              visible: true,
            });

            (groupLayer as any).ownerOrder = selectedOrderId;
            (groupLayer as any).fileId = _id;

            return [groupLayer];
          }
          return [];
        } catch (err) {
          console.error('Error creating Shapefile layers:', err);
          return [];
        }
      };

      const [geoDbLayers, shpLayers] = await Promise.all([
        createGeodatabaseLayers(),
        createShapefileLayers(),
      ]);

      if (!active) return;

      geodatabaseLayersRef.current = geoDbLayers;
      shapefileLayersRef.current = shpLayers;

      // Update window visibility getters
      (window as any).geodatabaseLayers = geoDbLayers;
      (window as any).shapefileLayers = shpLayers;

      if (lasMapInstanceRef.current && lasViewRef.current) {
        lasViewRef.current.goTo(geoDbLayers);
        lasMapInstanceRef.current.addMany([...geoDbLayers, ...shpLayers]);
      }
    };

    loadDataLayers();

    return () => {
      active = false;
    };
  }, [selectedOrderId, tokenRegistered, !!lasViewref]);

  // Adding Pano layer on map
  useEffect(() => {
    const map = lasViewref?.map;
    if (!tokenRegistered || !map || !panoFeatures || panoFeatures.length === 0) {
      if (panoLayerFeatureRef.current && map) {
        map.remove(panoLayerFeatureRef.current);
        panoLayerFeatureRef.current = null;
        panoLayerRef.current = null;
        setPanoControlsEnabled(false);
      }
      lastPanoStringRef.current = '';
      return;
    }

    const panoString = JSON.stringify(panoFeatures);
    if (panoString === lastPanoStringRef.current && map === lastMapUsedRef.current && panoLayerFeatureRef.current) {
      return; // avoid unnecessary re-rendering
    }

    // Remove old layer
    if (panoLayerFeatureRef.current && map) {
      map.remove(panoLayerFeatureRef.current);
    }

    const panofeatureCollection = {
      type: 'FeatureCollection',
      features: panoFeatures,
    };
    const panoBlob = new Blob([JSON.stringify(panofeatureCollection)], {
      type: 'application/json',
    });
    const panoUrl = URL.createObjectURL(panoBlob);
    const panoLayer = new GeoJSONLayer({
      url: panoUrl,
      title: 'Pano Features',
      outFields: ['*'],
      renderer: {
        type: 'simple',
        symbol: {
          type: 'simple-marker',
          style: 'circle',
          size: 8,
          color: [1, 161, 221, 1],
          outline: {
            color: [0, 55, 76, 1],
            width: 1,
          },
        },
      },
      visible: showPanoLayer,
    });
    panoLayer.elevationInfo = {
      mode: 'on-the-ground',
    };
    map.add(panoLayer);
    panoLayerFeatureRef.current = panoLayer as any;
    panoLayerRef.current = panoLayer;
    panoLayer.when(() => {
      panoLayer.visible = panoVisible;
    });
    setPanoControlsEnabled(true);
    lastPanoStringRef.current = panoString;
    lastMapUsedRef.current = map;

    return () => {
      if (panoLayerFeatureRef.current && map) {
        map.remove(panoLayerFeatureRef.current);
      }
      URL.revokeObjectURL(panoUrl);
      panoLayerFeatureRef.current = null;
      panoLayerRef.current = null;
    };
  }, [tokenRegistered, panoFeatures, showPanoLayer, lasViewref]);

  useEffect(() => {
    if (!selectedOrderId || !lasViewref) return;

    // Remove existing widget if any
    if (annotationWidgetRef3d.current) {
      lasViewref.ui.remove(annotationWidgetRef3d.current);
      annotationWidgetRef3d.current.destroy();
      annotationWidgetRef3d.current = null;
    }

    const AnnotationEdit3d = new Annotation({
      view: lasViewref,
      clicked: true,
      otherwidgetClicked: false,
    });
    annotationWidgetRef3d.current = AnnotationEdit3d;
    setAnnotationEdit(AnnotationEdit3d);
    lasViewref.ui.add(AnnotationEdit3d, 'top-right');
  }, [selectedOrderId, lasViewref]);

  // Memoize KML string to prevent unnecessary re-renders. Only the sidebar-selected order's
  // boundary is serialized; an empty result returns '' so the layer-management effect removes
  // the layer outright. An order switch changes the string and redraws the boundary (AUT-6431).
  const kmlString = useMemo(() => {
    if (!featureCollection?.features?.length) return '';
    const selectedBoundaries = selectOrderBoundaries(featureCollection, selectedOrderId);
    if (!selectedBoundaries.features.length) return '';
    return JSON.stringify(selectedBoundaries);
  }, [featureCollection, selectedOrderId]);

  // Adding KML layer on map
  useEffect(() => {
    const map = lasViewref?.map;

    // Handle empty/no data case
    if (!tokenRegistered || !map || !kmlString) {
      if (geoLayerRef.current && map) {
        map.remove(geoLayerRef.current);
        geoLayerRef.current = null;
        setGeoLayer(null);
      }
      lastKmlStringRef.current = '';
      return;
    }

    // Skip if data hasn't changed and layer exists
    if (kmlString === lastKmlStringRef.current && geoLayerRef.current) {
      return;
    }

    // Remove old layer before creating new one
    if (geoLayerRef.current && map) {
      map.remove(geoLayerRef.current);
      geoLayerRef.current = null;
    }

    const blob = new Blob([kmlString], { type: 'application/json' });
    const geoJsonUrl = URL.createObjectURL(blob);

    const newGeoLayer = new GeoJSONLayer({
      url: geoJsonUrl,
      title: 'KML Features',
      // The selected order's boundary in the viewer's long-standing bold teal (AUT-6431).
      renderer: boundaryRenderer as unknown as __esri.SimpleRenderer,
      // Honors the sidebar checkbox from the first frame, so a rebuilt layer (order switch)
      // doesn't flash a boundary the user turned off.
      visible: kmlBoundaryVisible,
    });

    map.add(newGeoLayer);
    geoLayerRef.current = newGeoLayer;
    setGeoLayer(newGeoLayer);
    lastKmlStringRef.current = kmlString;

    // Only revoke URL on cleanup, don't remove layer ref
    return () => {
      URL.revokeObjectURL(geoJsonUrl);
    };
  }, [tokenRegistered, kmlString, lasViewref]);

  // Sync the Khora "Dashboard layers" slice → the 3D scene. One GeoJSONLayer per chat-delivered
  // layer, loaded from a presigned S3 url (large) or inline geojson via a blob (small). Deduped by id
  // (the slice already dedupes) so a repeat/verify re-emit never stacks duplicates; a layer dropped
  // from the slice is removed and its blob URL revoked; visibility follows the slice. Features sit on
  // the terrain (elevationInfo on-the-ground) not at z=0. Esri's default renderer draws any geometry;
  // the layer color is shown in the Sidebar legend. (NOT wired through CinematicToolbar.)
  useEffect(() => {
    if (!KHORA_ENABLED) return; // master kill switch — never add Khora layers to the scene when off
    const map = lasViewref?.map;
    if (!map) return;
    const tracked = khoraLayersRef.current;

    // The parent effect rebuilds the SceneView (a NEW map) on order/project change and destroys the
    // old one. Detect that and reset the tracker so every layer re-adds to the new map — we must NOT
    // call remove() on the destroyed old map, and the "already tracked" check would otherwise skip
    // re-adding, silently orphaning the Khora layers.
    if (khoraMapRef.current !== map) {
      Object.values(tracked).forEach((entry) => { if (entry.blobUrl) URL.revokeObjectURL(entry.blobUrl); });
      Object.keys(tracked).forEach((id) => delete tracked[id]);
      khoraMapRef.current = map;
    }

    const wantedIds = viewerLayers.map((entry) => entry.id);
    Object.keys(tracked).forEach((id) => {
      if (wantedIds.includes(id)) return;
      const entry = tracked[id];
      if (entry) {
        map.remove(entry.layer);
        if (entry.blobUrl) URL.revokeObjectURL(entry.blobUrl);
      }
      delete tracked[id];
    });

    viewerLayers.forEach((spec) => {
      const signature = spec.url || (spec.geojson ? JSON.stringify(spec.geojson) : spec.id);
      const existing = tracked[spec.id];
      if (existing) {
        if (existing.signature === signature) {
          existing.layer.visible = spec.visible; // same data → just reflect visibility
          return;
        }
        // Data changed (a refined request / verify-pass re-emit updates the payload in place) → drop
        // the stale layer and rebuild it below with the new url/geojson.
        map.remove(existing.layer);
        if (existing.blobUrl) URL.revokeObjectURL(existing.blobUrl);
        delete tracked[spec.id];
      }
      let layerUrl = spec.url;
      let blobUrl: string | undefined;
      if (!layerUrl && spec.geojson) {
        const blob = new Blob([JSON.stringify(spec.geojson)], { type: 'application/json' });
        blobUrl = URL.createObjectURL(blob);
        layerUrl = blobUrl;
      }
      if (!layerUrl) return;
      const layer = new GeoJSONLayer({
        url: layerUrl,
        title: spec.title,
        outFields: ['*'],
        visible: spec.visible,
        elevationInfo: { mode: 'on-the-ground' },
      });
      // The click→popup handler reads these off the layer (buildFeatureDisplay): layerName/Color for
      // the popup header, khoraPopupFields to limit which attributes show.
      (layer as any).layerName = spec.title;
      (layer as any).layerColor = spec.color;
      (layer as any).khoraPopupFields = spec.popupFields || null;
      map.add(layer);
      tracked[spec.id] = { layer, blobUrl, signature };
      // Surface a load failure (expired presigned URL / CORS / bad geometry) by removing the dead
      // layer instead of leaving it lingering in the scene + the hitTest include set.
      layer.load().then(
        () => {
          // Draw in the layer's declared color once its geometryType is known, so the map matches the swatch.
          if (spec.color) {
            try { layer.renderer = analysisLayerRenderer(layer.geometryType, spec.color); } catch { /* keep default symbol */ }
          }
        },
        () => {
          // Load failed (expired presigned URL / CORS / bad geometry) — remove the dead layer.
          map.remove(layer);
          if (blobUrl) URL.revokeObjectURL(blobUrl);
          if (tracked[spec.id]?.layer === layer) delete tracked[spec.id];
        },
      );
    });
  }, [viewerLayers, lasViewref]);

  // Revoke any remaining Khora-layer blob URLs on unmount (the view is torn down separately).
  useEffect(() => () => {
    Object.values(khoraLayersRef.current).forEach((entry) => { if (entry.blobUrl) URL.revokeObjectURL(entry.blobUrl); });
    khoraLayersRef.current = {};
  }, []);

  // Adding Raster layers on map
  useEffect(() => {
    const map = lasViewref?.map;
    if (!tokenRegistered || !map || !rasterTileJson) {
      if (rasterLayers && rasterLayers.length > 0 && map) {
        rasterLayers.forEach((r) => map.remove(r.layer));
        setRasterLayers([]);
        (window as any).rasterLayers = [];
      }
      lastRasterStringRef.current = '';
      return;
    }

    const rasterString = JSON.stringify(rasterTileJson);
    if (rasterString === lastRasterStringRef.current && map === lastRasterMapRef.current) {
      return; // avoiding unnecessary re-rendering
    }

    // Remove old layers
    if (rasterLayers && rasterLayers.length > 0) {
      rasterLayers.forEach((r) => map.remove(r.layer));
    }

    const newRasterList: any[] = []; // array to store new raster layers
    Object.entries(rasterTileJson).forEach(([layerName, layerData]: [string, any]) => {
      const urlTemplate = layerData?.tileUrl;
      const isTms = layerData?.isTms ?? false;

      if (urlTemplate) {
        const rasterTileLayer = new RasterTileLayer({
          urlTemplate,
          isTms,
          opacity: 1,
          title: layerName,
        });
        map.add(rasterTileLayer, 0); // Put at the bottom
        newRasterList.push({ name: layerName, layer: rasterTileLayer });
      }
    });

    setRasterLayers(newRasterList);
    (window as any).rasterLayers = newRasterList; // storing raster layers in window object for easy access
    lastRasterStringRef.current = rasterString;
    lastRasterMapRef.current = map;
  }, [tokenRegistered, rasterTileJson, lasViewref]);

  // Adding Point Cloud layers on map
  useEffect(() => {
    const map = lasViewref?.map;
    if (!tokenRegistered || !map || !projectData?.lasFiles) {
      if (pointCloudLayersRef.current && pointCloudLayersRef.current.length > 0 && map) {
        pointCloudLayersRef.current.forEach((l) => map.remove(l));
        setPointCloudLayers([]);
        pointCloudLayersRef.current = [];
      }
      lastPointCloudStringRef.current = '';
      return;
    }

    const lasString = JSON.stringify(projectData.lasFiles);
    if (lasString === lastPointCloudStringRef.current && map === lastPointCloudMapRef.current) {
      return; // avoid unnecessary re-rendering
    }

    // Remove old layers
    if (pointCloudLayersRef.current && pointCloudLayersRef.current.length > 0) {
      pointCloudLayersRef.current.forEach((l) => map.remove(l));
      pointCloudLayersRef.current = [];
    }

    const newLayers = createPointCloudLayers();
    if (newLayers.length > 0) {
      map.addMany(newLayers);
      pointCloudLayersRef.current = newLayers;
      setPointCloudLayers(newLayers);

      // Sync checkedItems
      const initialCheckedItems = newLayers.reduce((acc, pcl) => {
        const title = pcl.title || 'PointCloud';
        acc[title] = pcl.visible;
        return acc;
      }, {} as { [key: string]: boolean });
      setCheckedItems((prev) => ({ ...prev, ...initialCheckedItems }));
      setLasFilesDisplayStatuses((prev) => ({ ...prev, ...initialCheckedItems }));
    }

    lastPointCloudStringRef.current = lasString;
    lastPointCloudMapRef.current = map;
  }, [tokenRegistered, projectData?.lasFiles, lasViewref, createPointCloudLayers, getLasTitle]);

  // AOI Widget for KML
  useEffect(() => {
    if (!geoLayer || !lasViewref || !featureCollection?.features?.length) return;

    const AOIWidget = new AOI({
      view: lasViewref,
    });
    setAoiWidget(AOIWidget);
    lasViewref.ui.add(AOIWidget, 'top-right');
    const handle = AOIWidget.on('AOI-toggle', ({ active }) => {
      setAoiToolActive(active);
    });

    return () => {
      if (lasViewref) {
        lasViewref.ui.remove(AOIWidget);
      }
      setAoiWidget(null);
      setAoiToolActive(false);
      handle.remove();
    };
  }, [geoLayer, lasViewref, featureCollection, AnnotationEdit]);

  // Single writer for the boundary layer's visibility: the sidebar checkbox AND-ed with the AOI
  // draw tool being idle. geoLayer in the deps re-applies the gate to a freshly rebuilt layer.
  useEffect(() => {
    if (geoLayerRef.current) {
      geoLayerRef.current.visible = kmlBoundaryVisible && !aoiToolActive;
    }
  }, [kmlBoundaryVisible, aoiToolActive, geoLayer]);

  // "Select area" region draw → query_region. A SketchViewModel rectangle over the 3D view yields a WGS84
  // region (bbox + polygon + acreage) stored in Redux, which buildAppState sends as app_state.selected_region
  // so Khora scopes "in this area / here" answers to it. Created once per view build, torn down with the view.
  const regionSketchRef = useRef<RegionSketchController | null>(null);
  const selectedRegion = useAppSelector((state) => state.dashboard.selectedRegion);
  const [isDrawingRegion, setIsDrawingRegion] = useState(false);
  useEffect(() => {
    // Khora-only machinery: don't even build the SketchViewModel outside the pilot gate.
    if (!khoraUiEnabled || !lasViewref) return undefined;
    const controller = createRegionSketchController(
      lasViewref, (region) => dispatch(setSelectedRegion(region)), setIsDrawingRegion,
      () => dispatch(setSelectedRegion(null)), // cancelled re-draw → drop the now-graphic-less phantom region
    );
    regionSketchRef.current = controller;
    return () => { controller.destroy(); regionSketchRef.current = null; };
  }, [khoraUiEnabled, lasViewref, dispatch]);
  // Keep the drawn rectangle in sync with Redux: when selectedRegion clears (the Clear button, or an
  // order/gdb switch via resetDashboardArtifacts), remove the graphic from the map too — mirroring the
  // chatHighlight reconciliation above, so a stale region never lingers into the next order. Redux is the
  // source of truth; this never fires mid-draw (selectedRegion only changes once a draw completes).
  useEffect(() => {
    if (lasViewref && !selectedRegion) regionSketchRef.current?.clear();
  }, [lasViewref, selectedRegion]);
  // Khora-initiated region commands (trigger_app_action draw_region/clear_region). 'draw' opens the sketch
  // in the requested shape, deactivating other draw tools first (like the "Select area" button); 'clear'
  // cancels any in-progress draw AND drops the region (the chatbot can't cancel a mid-draw sketch itself).
  // Keyed on the command nonce AND lasViewref (like the chatPano bridge) so a command that arrives before the
  // viewer is built still fires once it's ready, instead of being dropped. A view rebuild re-runs the effect,
  // but resetDashboardArtifacts nulls regionCommand on an order switch, so a stale command isn't re-fired.
  const regionCommand = useAppSelector((state) => state.dashboard.regionCommand);
  useEffect(() => {
    if (!KHORA_ENABLED || !regionCommand || !lasViewref) return;
    if (regionCommand.kind === 'clear') {
      regionSketchRef.current?.clear();   // cancels an in-progress draw + removes any drawn graphic
      dispatch(setSelectedRegion(null));  // also drop a completed region (reconciliation no-ops if already null)
      return;
    }
    InactiveWidgetClose({ name: 'regionSketch' }, true);
    regionSketchRef.current?.startDraw(regionCommand.mode);
  }, [regionCommand, lasViewref]);

  useEffect(() => {
    if (!homeWidget || !lasViewref) return;

    // To ensure Home is always at the bottom, we re-add it whenever
    // Annotation or AOI widgets might have been re-added.
    lasViewref.ui.remove(homeWidget);
    lasViewref.ui.add(homeWidget, 'top-right');
  }, [homeWidget, lasViewref, AnnotationEdit, aoiWidget]);

  useEffect(() => {
    if (panoLayerFeatureRef.current) {
      panoLayerFeatureRef.current.visible = panoVisible;
    }
  }, [panoVisible]);

  useEffect(() => {
    const panoLayer = panoLayerRef.current;
    if (!panoLayer) return;

    panoLayer.renderer = {
      type: 'unique-value',
      field: 'object_id',
      uniqueValueInfos: failedPanoIds.map((id) => ({
        value: id,
        symbol: {
          type: 'simple-marker',
          style: 'circle',
          size: 8,
          color: [160, 160, 160, 1], // GREY
          outline: {
            color: [120, 120, 120, 1],
            width: 1,
          },
        },
        label: 'Failed pano',
      })),
      defaultSymbol: {
        type: 'simple-marker',
        style: 'circle',
        size: 8,
        color: [1, 161, 221, 1], // NORMAL BLUE
        outline: {
          color: [0, 55, 76, 1],
          width: 1,
        },
      },
    };
  }, [failedPanoIds, panoFeatures]);

  // Keyed on the order id, not the order object, so an offset save on the same order does not fly the camera home.
  useEffect(() => {
    if (!homeWidget || !lasViewRef.current || !selectedFlightTarget) return;

    const view = lasViewRef.current;

    const { longitude, latitude } = selectedFlightTarget;

    const vp = new Viewpoint({
      camera: {
        position: {
          longitude,
          latitude,
          z: 1500,
        },
        heading: 0,
        tilt: 0,
      },
    });

    view.goTo(vp, {
      duration: 1500,
      easing: 'ease-in-out',
    });

    homeWidget.viewpoint = vp;
  }, [homeWidget, selectedOrderId, selectedFlightTarget?.longitude, selectedFlightTarget?.latitude]);

  useEffect(() => {
    const annotationwidget3d = annotationWidgetRef3d.current;
    if (!annotationwidget3d) return undefined;
    const handles: any[] = [];
    if (annotationwidget3d) {
      handles.push(annotationwidget3d.on('open-annotations', async () => {
        annotationWidgetRef3d.current.otherWidget = false;
        otherwidgetactiveref.current = false;
        InactiveWidgetClose(annotationWidgetRef3d.current, true);
      }));
      handles.push(annotationwidget3d.on('annotate-toggle', async ({ active }) => {
        annotationWidgetRef3d.current.otherWidget = false;
        isAddingRef3d.current = active;
        ifStart.current = false;
        setIsAdding(active);
        setAnnotations3d([]);
        existingAnnotaion.current = [];
      }));
      handles.push(annotationwidget3d.on('annotate-start', async ({ active }) => {
        annotationWidgetRef3d.current.otherWidget = false;
        (window as any).newannotationclicked = true;
        if (lasViewref?.container) lasViewref.container.style.cursor = 'crosshair';
        isAddingRef3d.current = true;
        ifStart.current = true;
        newLabelRef.current = true;
        const lasrect = lasRef.current.getBoundingClientRect();
        setOverlaySize3d({
          width: lasrect.width,
          height: lasrect.height,
        });
        setOverlayPosition3d({
          top: lasrect.top,
          left: lasrect.left,
        });
        setIsAdding(active);
        setAnnotations3d([]);
        existingAnnotaion.current = [];
      }));
      handles.push(annotationwidget3d.on('fetch-annotations', async () => {
        const annotateOrder = orderref.current || orderid;
        const match = AnnotationFeatures[annotateOrder];
        if (match) {
          const result = match;
          isAddingRef3d.current = true;
          setIsAdding(true);
          const { features } = result;
          if (result?.features) {
            setAnnotations3d(features);
            existingAnnotaion.current = features;
            newLabelRef.current = false;
            const lasrect = lasRef.current.getBoundingClientRect();
            setOverlaySize3d({
              width: lasrect.width,
              height: lasrect.height,
            });
            setOverlayPosition3d({
              top: lasrect.top,
              left: lasrect.left,
            });
          }
        }
      }));
      handles.push(annotationwidget3d.on('hide-annotations', () => {
        isAddingRef3d.current = false;
        setIsAdding(false);
        ifStart.current = false;
        newLabelRef.current = true;

        isAddingRef.current = true;
        const lasrect = lasRef.current.getBoundingClientRect();
        setOverlaySize3d({
          width: 0,
          height: 0,
        });
        setOverlayPosition3d({
          top: lasrect.top,
          left: lasrect.left,
        });
        setAnnotations3d([]);
        existingAnnotaion.current = [];
      }));
    }
    // Remove every handler on re-run (AnnotationFeatures/AnnotationEdit change): the effect re-binds the
    // SAME widget instance, so without this the five listeners accumulate and old closures over a stale
    // AnnotationFeatures keep firing. Mirrors the distance/area sibling effects below.
    return () => handles.forEach((h) => h.remove());
  }, [AnnotationFeatures, AnnotationEdit]);

  useEffect(() => {
    if (!distanceWidget3d) return;

    const handles: any[] = [];

    if (distanceWidget3d) {
      handles.push(
        distanceWidget3d.on('measurement-toggle', (e: { active: boolean }) => {
          measuringRefDistance.current = e.active;
          SetMeasurmentActiveDis(e.active);
          SetMeasurmentActiveArea(!e.active);
          InactiveWidgetClose(distanceWidget3d, true);
          if (e.active) setShowPopup(false);
        }),
      );
    }

    return () => handles.forEach((h) => h.remove());
  }, [distanceWidget3d]);

  useEffect(() => {
    if (!AreaWidget3d) return;
    const handles: any[] = [];

    if (AreaWidget3d) {
      handles.push(
        AreaWidget3d.on('measurement-toggle', (e: { active: boolean }) => {
          measuringRefArea.current = e.active;
          SetMeasurmentActiveArea(e.active);
          SetMeasurmentActiveDis(!e.active);
          InactiveWidgetClose(AreaWidget3d, true);
          if (e.active) setShowPopup(false);
        }),
      );
    }
    return () => handles.forEach((h) => h.remove());
  }, [AreaWidget3d]);

  useEffect(() => {
    if (!basemapRef.current) return;
    const handles: any[] = [];

    if (basemapRef.current) {
      handles.push(
        basemapRef.current.on('basemap-toggle', (e: { active: boolean }) => {
          InactiveWidgetClose(basemapRef.current, true);
          basemapRef.current.isActive = true;
          if (lasViewref?.container) lasViewref.container.style.cursor = 'default';
          (window as any).basemapOpened = true;
        }),
        basemapRef.current.on('basemap-toggle-close', (e: { active: boolean }) => {
          basemapRef.current.isActive = false;
          (window as any).basemapOpened = false;
        }),
      );
    }
    return () => handles.forEach((h) => h.remove());
  }, [basemapRef.current]);

  // handle panoLayer visibility
  useEffect(() => {
    const panoLayer = panoLayerRef.current;
    const coneGraphic = coneGraphicRef.current;

    if (panoLayer) panoLayer.visible = panoVisible;
    if (coneGraphic) coneGraphic.visible = panoVisible;
  }, [panoVisible]);

  // Cleanup sliders
  useEffect(() => () => {
    [inputSliderRef, outputSliderRef, classSliderRef].forEach((ref) => {
      ref.current?.destroy();
    });
  }, []);

  useEffect(() => {
    if (!selectedOrderId || !lasViewRef.current) return;

    const selectedOrder = sortedPlacedOrders.find((o) => o._id === selectedOrderId);
    if (!selectedOrder) return;
    const cadIdsForOrder = (selectedOrder.cadFiles ?? []).map((c: any) => c._id);
    if (!cadIdsForOrder.length) {
      // If no CAD files, hide the feature layers
      if (featureLayers && featureLayers.length > 0) {
        featureLayers.forEach((layer) => {
          if (layer) layer.visible = false;
        });
      }
    }

    // Update definition expressions for all layers with proper null checks
    const updateLayerDefs = (siteIds: string[]) => {
      const whereClause = `site_id = '${siteIds}' AND has3d = true`;

      // Update FeatureLayers with null checks
      if (featureLayers && featureLayers.length > 0) {
        featureLayers.forEach((layer) => {
          if (layer && layer.definitionExpression !== undefined) {
            try {
              layer.definitionExpression = whereClause;
            } catch (err) {
              console.warn('Failed to set definition expression on feature layer:', err);
            }
          }
        });
      }
    };

    // updateLayerDefs(cadIdsForOrder);
    const activeSiteId = cadIdsForOrder[cadIdsForOrder.length - 1];
    if (activeSiteId) {
      updateLayerDefs(activeSiteId); //single siteId only
    }

    // Update point cloud layers visibility
    if (pointCloudLayers && pointCloudLayers.length > 0) {

      pointCloudLayers.forEach((layer) => {
        if (!layer) return;

        const lasFile = projectData?.lasFiles?.find(
          (f: any) => getLasTitle(f) === layer.title,
        );

        // For classified point clouds, manage auto-visibility on order switch
        if (!lasFile || lasFile.lasType === 'unclassified') return;
        if (lasFile.ownerOrder !== selectedOrderId) return;
        if (layer.loaded) {
          layer.filters = [];
          layer.definitionExpression = '';
        } else {
          layer.when(() => {
            layer.filters = [];
            layer.definitionExpression = '';
          });
        }
      });
    }

    // Query and apply rendering for each CAD
    if (selectedOrder && selectedOrder.orderStatus === orderStatusCode.done) {
      cadIdsForOrder.forEach((cadId) => {
        if ((window as any).queryLayersTable) {
          try {
            (window as any).queryLayersTable(cadId);
          } catch (err) {
            console.warn(`Failed to query layers table for CAD ${cadId}:`, err);
          }
        }
      });
    }

    if (geodatabaseLayersRef.current && geodatabaseLayersRef.current.length > 0) {
      geodatabaseLayersRef.current.forEach((layer) => {
        const shouldBeVisible = (layer as any).ownerOrder === selectedOrderId;
        layer.visible = shouldBeVisible;
      });
    }

    // Update Shapefile layers visibility
    if (shapefileLayersRef.current && shapefileLayersRef.current.length > 0) {
      shapefileLayersRef.current.forEach((layer) => {
        const shouldBeVisible = (layer as any).ownerOrder === selectedOrderId;
        layer.visible = shouldBeVisible;
      });
    }
    lastOrderIdRef.current = selectedOrderId;
  }, [selectedOrderId, placedOrders, featureLayers, projectData?.lasFiles, getLasTitle]);

  /// Widget Toggle Function
  const InactiveWidgetClose = (widget: any, state: boolean) => {
    // get widget
    const widgetState = state;

    const widgetManager = {
      distance: distanceWidget3d,
      area: AreaWidget3d,
      basemap: basemapRef.current,
      annotation: annotationWidgetRef3d.current,
    };

    Object.values(widgetManager).forEach((Widget) => {
      if (!Widget || Widget.name === widget.name) return;

      Widget.isActive = !widgetState;

      if (Widget.name === 'distanceWidget3d') {
        distanceWidget3d?.measurement?.viewModel?.clear();
      } else if (Widget.name === 'AreaWidget3d') {
        AreaWidget3d?.measurement?.viewModel?.clear();
      } else if (Widget.name === 'basemapWidget') {
        Widget.isActive = false;
        basemapRef.current.isActive = false;
        (window as any).basemapOpened = false;
      } else if (Widget.name === 'AnnotationEdit') {
        Widget.isActive = false;
        widget.showExisting = false;
        Widget.showPanel = false;
        Widget.otherWidget = true;
        Widget._closePanel();
        newLabelRef.current = false;
        otherwidgetactiveref.current = true;
      }
    });
    // A newly-activated draw tool aborts any IN-PROGRESS region draw so two draw interactions never compete
    // for pointer input; a COMPLETED region (its graphic + Redux selection) is left intact.
    if (widget?.name !== 'regionSketch') regionSketchRef.current?.cancelDraw();
  };

  const handleViewVisibility = (view: string) => {
    const views = { las: lasVisible, pano: panoVisible };
    const visibleCount = Object.values(views).filter(Boolean).length;
    if (visibleCount === 1 && views[view]) return;
    // Toggle the selected view
    if (view === 'pano') {
      setPanoVisible((prev) => !prev);
    }
  };
  const containerWidth = useMemo(() => {
    const count = [lasVisible, panoVisible].filter(Boolean).length;

    if (count === 1) return 1000;
    if (count === 2) return 500;

    return 1000; // fallback
  }, [panoVisible]);
  async function getFullLayersExtent(view) {
    await reactiveUtils.whenOnce(() => view.ready);
    let pointCloudExtent: Extent | null = null;
    let tileExtent: Extent | null = null;

    const layers = view.map.layers.toArray();

    for (const layer of layers) {
      try {
        await layer.when();
        const extent = layer.fullExtent || layer.extent;
        if (!extent) continue;

        // Skip global generic extents (oversized services)
        if (extent.width > 1000000 || extent.height > 1000000) {
          continue;
        }
        if (
          layer.type === 'point-cloud' ||
          layer.type === 'scene'
        ) {
          pointCloudExtent = pointCloudExtent
            ? pointCloudExtent.union(extent)
            : extent.clone();
          continue;
        }

        if (
          layer.type === 'base-tile' ||
          layer.type === 'raster' ||
          layer.type === 'imagery'
        ) {
          tileExtent = tileExtent
            ? tileExtent.union(extent)
            : extent.clone();
          continue;
        }

        // FeatureLayers (CAD, KML, Geodb, Shp)
        if (layer.type === 'feature' || layer.type === 'geojson') {
          tileExtent = tileExtent
            ? tileExtent.union(extent)
            : extent.clone();
        }
      } catch {

      }
    }

    if (pointCloudExtent && tileExtent) {
      return pointCloudExtent.union(tileExtent);
    }
    return pointCloudExtent || tileExtent || null;
  }
  // call labeleditor function
  const showLoadingOverlay = loading || !viewReady;

  // Advance the rotating loading messages while the overlay is shown, holding on
  // the last message once reached.
  useEffect(() => {
    if (!showLoadingOverlay) {
      setLoadingMessageIndex(0);
      return undefined;
    }
    const id = setInterval(() => {
      setLoadingMessageIndex((i) => Math.min(i + 1, LOADING_MESSAGES.length - 1));
    }, LOADING_MESSAGE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [showLoadingOverlay]);

  const loadingContent = (
    <>
      <CircularProgress />
      <Typography sx={styles.loadingMessage}>
        {LOADING_MESSAGES[loadingMessageIndex]}
      </Typography>
      <Typography sx={styles.loadingSubtext}>
        {LOADING_SUBTEXT}
      </Typography>
    </>
  );

  if (loadFailure) return <ProjectNotFound failure={loadFailure} onRetry={retryLoad} />;

  return (
    <>
      {/* Khora chat (launcher + drawer, pilot-gated inside). Mounted here — not in the Shell — so the
          app frame carries no per-navigation Khora code; independent of `loading` so the saved
          conversation can restore while the scene loads. KHORA_ENABLED is the master kill switch. */}
      {KHORA_ENABLED && <KhoraChatbot />}
      {/* Data-load phase only (sidebar + map not mounted yet): brief full-area
          overlay. Once data is loaded the sidebar renders immediately and the
          loading screen is scoped to just the map (inside #esridiv below). */}
      {loading && (
        <div style={styles.loadingOverlay}>{loadingContent}</div>
      )}
      {!loading && (
        <>
          <FilesSidebar
            key={selectedOrderId}
            lasFilesWith3DTile={projectData?.lasFiles || []}
            showPointCloud={showPointCloud}
            setShowPointCloud={setShowPointCloud}
            showClassifiedPointCloud={showClassifiedPointCloud}
            setShowClassifiedPointCloud={setShowClassifiedPointCloud}
            setResetZoom={setResetZoom}
            toggleLasVisibility={toggleLasVisibility}
            lasFilesDisplayStatuses={lasFilesDisplayStatuses}
            onToggleSidebar={setIsSidebarVisible}
            isSidebarVisible={isSidebarVisible}
            showPanoLayer={showPanoLayer}
            setShowPanoLayer={setShowPanoLayer}
            toggleTerrain={toggleTerrain}
            classVisibility={classVisibility}
            classesPresentByFile={classesPresentByFile}
            toggleUnknownClassVisibility={toggleUnknownClassVisibility}
            toggleClassVisibility={toggleClassVisibility}
            setAllClassesVisibility={setAllClassesVisibility}
            openAdjustPanel={openAdjustPanel}
            onAdjustPanelPositionChange={setAdjustPanelPosition}
            view={lasViewref}
            geodatabaseLayersRef={geodatabaseLayersRef}
            shapefileLayersRef={shapefileLayersRef}
            kmlBoundaryVisible={kmlBoundaryVisible}
            kmlBoundaryAvailable={Boolean(kmlString)}
            onKmlBoundaryToggle={toggleKmlBoundaryVisibility}
            userElevationOffset={userElevationOffset}
            onCommitUserElevationOffset={commitUserElevationOffset}
            projectGeoidOffset={projectGeoidOffset}
            pendingGeoidState={pendingGeoidState}
            geoidStatus={geoidStatus}
            geoidUnavailableReason={geoidUnavailableReason}
            elevationSaveFailed={elevationSaveFailed}
          />
          {/* CinematicToolbar removed per request; its chat→map highlight effect is relocated above. */}
          {/* Output dashboard — KPIs/charts computed from the loaded geodatabase layers. Gated by the
              shared Khora gate (master switch + pilot project + ?khora=off) like every Khora surface. */}
          {khoraUiEnabled && <Dashboard view={lasViewref} geodatabaseLayersRef={geodatabaseLayersRef} selectedFeature={selectedFeature} />}
          {/* CLASSIFIED POINT CLOUD PANEL */}
          {classifiedPanelOpen && (
            <ClickAwayListener
              key="classified-panel"
              onClickAway={() => {
                setClassifiedPanelOpen(false);
                setAdjustPanelPosition(null);
              }}
            >
              <Box
                sx={{
                  ...styles.clickaway, ...getAdjustPanelStyles(styles.classifiedclickaway),
                }}
              >
                <Box sx={styles.pointsizeslidebox}>
                  <Box sx={styles.pointsizeslidetypo}>
                    <Typography variant="body2" component="span">
                      Classified Point Cloud
                    </Typography>
                    <Typography variant="body2" component="span" sx={styles.maxValueLabel}>
                      {classifiedPointSize % 1 === 0 ? classifiedPointSize.toFixed(0) : classifiedPointSize.toFixed(1)}
                    </Typography>
                  </Box>
                  <Box id="classified-point-size-slider" key="classified-slider" sx={styles.pointercolor} />
                </Box>
              </Box>
            </ClickAwayListener>
          )}

          {/* OUTPUT 3D POINTS PANEL (AERIAL) */}
          {outputPanelOpen && (
            <ClickAwayListener
              key="output-panel"
              onClickAway={() => {
                setOutputPanelOpen(false);
                setAdjustPanelPosition(null);
              }}
            >
              <Box
                sx={{
                  ...styles.clickaway, ...getAdjustPanelStyles(styles.output3d),
                }}
              >
                <Box sx={styles.pointsizeslidebox}>
                  <Box sx={styles.pointsizeslidetypo}>
                    <Typography variant="body2" component="span">
                      Output 3D Points
                    </Typography>
                    <Typography variant="body2" component="span" sx={styles.maxValueLabel}>
                      {outputPointSize % 1 === 0 ? outputPointSize.toFixed(0) : outputPointSize.toFixed(1)}
                    </Typography>
                  </Box>
                  <Box id="output-point-size-slider" key="output-slider" sx={styles.pointercolor} />
                </Box>
              </Box>
            </ClickAwayListener>
          )}

          {/* INPUT POINT CLOUD PANEL */}
          {inputPanelOpen && (
            <ClickAwayListener
              key="input-panel"
              onClickAway={() => {
                setInputPanelOpen(false);
                setAdjustPanelPosition(null);
              }}
            >
              <Box
                sx={{
                  ...styles.clickaway, ...getAdjustPanelStyles(styles.unclassifiedclickaway),
                }}
              >
                <Box sx={styles.pointsizeslidebox}>
                  <Box sx={styles.pointsizeslidetypo}>
                    <Typography variant="body2" component="span">
                      Input Point Cloud
                    </Typography>
                    <Typography variant="body2" component="span" sx={styles.maxValueLabel}>
                      {inputPointSize % 1 === 0 ? inputPointSize.toFixed(0) : inputPointSize.toFixed(1)}
                    </Typography>
                  </Box>
                  <Box id="input-point-size-slider" key="input-slider" sx={styles.pointercolor} />
                </Box>
              </Box>
            </ClickAwayListener>
          )}
          {/* Document Icon on Map View - Initially hidden, shown when sidebar is hidden */}
          <IconButton
            onClick={() => setIsSidebarVisible(!isSidebarVisible)}
            sx={styles.mapViewToggleButton(isSidebarVisible)}
            aria-label={isSidebarVisible ? 'Hide Sidebar' : 'Show Sidebar'}
          >
            <SvgIcon sx={styles.sidebarsvg}>
              <rect fill="transparent" width="24" height="24" />
              <polygon
                points="15.83,2.5 4.17,2.5 2.5,4.17 2.5,15.83 4.17,17.5 15.83,17.5 17.5,15.83 17.5,4.17"
                fill="none"
                stroke="#021E40"
                strokeWidth="1.46"
              />
              <polygon
                points="7.5,2.5 7.5,17.5"
                fill="none"
                stroke="#021E40"
                strokeWidth="1.46"
              />
              <polygon
                points="13.33,12.5 10.83,10 13.33,7.5"
                fill="none"
                stroke="#021E40"
                strokeWidth="1.46"
              />
            </SvgIcon>
          </IconButton>
        </>
      )}
      <div id="esridiv" style={{ ...styles.esriView, display: loading ? 'none' : 'flex' }}>
        {/* Loading screen scoped to just the map region (right side); the
            sidebar stays visible and usable while the SceneView renders. */}
        {!viewReady && (
          <div style={styles.loadingOverlay}>
            {tokenStatus === 'failed' ? <ViewerTokenError onRetry={retryToken} /> : loadingContent}
          </div>
        )}
        <div style={styles.lasView}>
          <div id="lasDiv" ref={lasRef} style={{ width: '100%', height: '100%' }} />
          {/* "Select area" region draw for Khora's query_region — a small floating chip over the 3D view. */}
          {khoraUiEnabled && lasViewref && (
            <Box sx={{
              position: 'absolute', top: 12, left: 12, zIndex: 4, display: 'flex', gap: 0.75, alignItems: 'center',
              bgcolor: 'rgba(255,255,255,0.92)', borderRadius: 1, boxShadow: 2, px: 1, py: 0.25,
            }}
            >
              {isDrawingRegion ? (
                <>
                  <Typography variant="caption" sx={{ color: '#153B5A', fontWeight: 600 }}>
                    Drag a rectangle…
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => regionSketchRef.current?.cancelDraw()}
                    sx={{ minWidth: 0, px: 0.5, textTransform: 'none', color: '#01A1DD' }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (selectedRegion ? (
                <>
                  <Typography variant="caption" sx={{ color: '#153B5A', fontWeight: 600 }}>
                    {`Area selected · ~${selectedRegion.area_acres} ac`}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => { InactiveWidgetClose({ name: 'regionSketch' }, true); regionSketchRef.current?.startEdit(); }}
                    sx={{ minWidth: 0, px: 0.5, textTransform: 'none', color: '#153B5A' }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    onClick={() => dispatch(setSelectedRegion(null))}
                    sx={{ minWidth: 0, px: 0.5, textTransform: 'none', color: '#01A1DD' }}
                  >
                    Clear
                  </Button>
                </>
              ) : (
                <Button
                  size="small"
                  onClick={() => { InactiveWidgetClose({ name: 'regionSketch' }, true); regionSketchRef.current?.startDraw('rectangle'); }}
                  sx={{ textTransform: 'none', color: '#153B5A', fontWeight: 600 }}
                >
                  ▭ Select area
                </Button>
              ))}
            </Box>
          )}
          {lasViewref && (
            <div style={styles.OverlaylasView(overlaySize3d, overlayposition3d)}>
              <LabelEditor
                view={lasViewref}
                active={isAddingRef3d.current}
                annotations={ifStart.current ? annotations3d : annotations3d}
                orderId={orderref.current}
                newLabel={newLabelRef.current}
                stopnewLabel={false}
                viewWidthNew={containerWidth}
                otherwidgetactive={otherwidgetactiveref.current}
                basemapactive={basemapRef.current.isActive}
              />
            </div>
          )}
          {/* Bottom attribute-table of whatever Khora currently displays; collapsed to a pill until opened. */}
          {khoraUiEnabled && lasViewref && (
            <GdbRowsPanel
              view={lasViewref}
              highlight={chatHighlight}
              queryResult={chatQueryResult}
              getLayers={() => geodatabaseLayersRef.current}
              gdbReadyKey={loadedGdbId}
            />
          )}
        </div>
        <div style={styles.panoView}>
          <div id="panoDiv" ref={panoRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
            {/* Injecting CSS to hide Pannellum library controls */}
            <style>{styles.panoControlsCss}</style>
            {panoVisible && imageSource ? (
              <ReactPannellum
                key={imageSource}
                id="1"
                sceneId="firstScene"
                imageSource={imageSource}
                config={{ ...config.panoConfig, yaw: initialYawRef.current }}
                style={styles.panoViewDiv}
                onPanoramaMouseDown={handlePanoMouseDown}
                onPanoramaMouseUp={handlePanoMouseUp}
                onLoad={async () => {
                  ReactPannellum.resize();
                }}
              />
            ) : (
              <div style={styles.placeholderWrapper}>
                <div style={styles.placeholderCard}>
                  <div style={styles.placeholderIcon}>{'\u2139'}</div>
                  <div>
                    <div style={styles.placeholderTitle}>Choose a start point</div>
                    <div style={styles.placeholderSubtitle}>
                      Select a point on the map to begin.
                    </div>
                  </div>
                </div>
              </div>
            )}
            {khoraUiEnabled && panoVisible && (
              // Standard AirWorks toggle (mirrors the dashboard's "Current view only" switch): a MUI
              // Switch + caption Typography on a translucent chip so it stays legible over the 360° image.
              <Box
                title="Let Khora analyze this 360° pano — attaches the current view to your next question"
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  zIndex: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  pl: 1,
                  pr: 1.25,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: 'rgba(255,255,255,0.92)',
                  boxShadow: '0 1px 4px rgba(21,59,90,0.25)',
                }}
              >
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={(
                    <Switch
                      size="small"
                      checked={panoVisionEnabled}
                      onChange={(event) => setPanoVisionEnabled(event.target.checked)}
                    />
                  )}
                  label={<Typography variant="caption" sx={{ color: '#153B5A', fontWeight: 600 }}>Analyze with Khora</Typography>}
                />
                {viewHeadingDeg !== null && (
                  // Center heading of the red view-cone — makes Khora's "pan ~N°" guidance actionable.
                  <Typography
                    variant="caption"
                    title="Center heading of the red view-cone (0°=N, 90°=E) — rotate the pano to change it"
                    sx={{ color: '#153B5A', pl: 0.5, pb: 0.25, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {`Looking ${viewHeadingDeg}° · ${compass8(viewHeadingDeg)}`}
                  </Typography>
                )}
              </Box>
            )}
            {panoVisible && navArrows.length > 0 && (
              <div style={styles.panoCompass} ref={compassRef}>
                {navArrows.map((n) => (
                  <div
                    key={n.sceneId}
                    role="button"
                    tabIndex={0}
                    aria-label={`Navigate to pano ${Math.round(n.dist)} m away`}
                    style={styles.panoCompassArrow}
                    onClick={() => {
                      if (!lasViewRef.current) return;
                      loadPanoScene(n.feature, panoLayerFeatureRef.current, n.sceneId, true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter' && e.key !== ' ') return;
                      if (!lasViewRef.current) return;
                      loadPanoScene(n.feature, panoLayerFeatureRef.current, n.sceneId, true);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          {/* Pano Popup Overlay */}
          {(panoError || panoWarning) && !panoErrorClosed && (
            <div style={styles.panoErrorOverlay}>
              <div style={styles.panoErrorPopup}>
                <div style={panoWarning ? styles.WarningPlaceholderIcon : styles.ErrorplaceholderIcon}>
                  {panoWarning ? 'i' : '!'}
                </div>
                <div>
                  <div style={styles.placeholderTitle}>{panoError || panoWarning}</div>
                  <div style={styles.placeholderSubtitle}>
                    Contact
                    {' '}
                    <a
                      href={config.airworks_SupportUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.supportLink}
                    >
                      support
                    </a>
                    {' '}
                    if the error persists.
                  </div>
                </div>
                <button
                  style={styles.errorCloseBtn}
                  onClick={() => { setPanoError(null); setPanoWarning(null); setPanoErrorClosed(true); }}
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>

        {filteredFeatures.length > 0 && showPopup && islasViewRef.current && filteredFeatures[currentIndex] && (
          <PopupWindow
            x={popupXY.x}
            y={popupXY.y}
            attributes={filteredFeatures[currentIndex]}
            index={currentIndex}
            totalCount={filteredFeatures.length}
            onPrev={() => {
              const newIndex = currentIndex - 1;
              setCurrentIndex(newIndex);
              if ((window as any).__highlightFeature) {
                (window as any).__highlightFeature(newIndex);
              }
            }}
            onNext={() => {
              const newIndex = currentIndex + 1;
              setCurrentIndex(newIndex);
              if ((window as any).__highlightFeature) {
                (window as any).__highlightFeature(newIndex);
              }
            }}
            onClose={() => {
              setShowPopup(false);
              const hl = lasViewRef.current?.map?.findLayerById('highlight-layer') as any;
              if (hl) hl.removeAll();
              (window as any).__highlightFeature = null;
            }}
            parentRef={null}
          />
        )}

        <div
          id="selectView"
          style={styles.selectView}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Tooltip */}
          {!panoControlsEnabled && isHovered && (
            <div style={styles.noPanoTooltip}>
              No pano available
            </div>
          )}

          <SVG
            src={panoVisible ? eyeHidden : eyeVisible}
            onClick={
              panoControlsEnabled ? () => handleViewVisibility('pano') : undefined
            }
            style={panoVisible ? styles.eyeHidden : styles.eyeVisible}
          />

          <button
            onClick={
              panoControlsEnabled ? () => handleViewVisibility('pano') : undefined
            }
            style={panoVisible ? styles.toggleButton : styles.toggleButtonOff}
          >
            {panoVisible ? 'HIDE PANO' : 'SHOW PANO'}
          </button>
        </div>

      </div>
    </>
  );
};

export default EsriSplitViewer;
